<script setup>
// Add these imports and refs for line items functionality
import {
	onMounted,
	ref,
	reactive,
	watch,
	nextTick,
	computed,
	useTemplateRef,
} from "vue";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";
import flatpickr from "flatpickr";
import { useRouter, useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import { Country, State, City } from "country-state-city";

// Add refs for port agents datalists
const originPortAgents = ref([]);
const destinationPortAgents = ref([]);
const isLoadingOriginPortAgents = ref(false);
const isLoadingDestinationPortAgents = ref(false);

// Add this with your other refs at the top of the script section
const serviceTypes = ref([]);
const isLoadingServiceTypes = ref(false);

// Add these refs near the top with your other refs
const containerTypes = ref([]);
const isLoadingContainerTypes = ref(false);

// Add this near the top of your script setup section with other refs
const isModalOpen = ref(false);
const selectedLineItemIndex = ref(0);

const router = useRouter();
const route = useRoute(); // Get the current route to access params
const isLoading = ref(true);
const isEditMode = ref(false); // Track if we're in edit mode (has UUID)
const shipmentUuid = ref(null); // Store the UUID if available
const isCompleted = ref(false);

const progress = ref(1);
const proNumber = ref(null);

const isLoadingProNumber = ref(false);
const showRegistrationCompleteModal = ref(false);

// Controls the visibility of the modal
const showActionModal = ref(false);
const fileBlob = ref(null);
const fileInputTag = ref(null);

function toggleActionModal() {
	showActionModal.value = !showActionModal.value;
}

function downloadTemplate() {
	// Replace this URL with the actual template URL when available
	const templateUrl = "/templates/template.csv";

	// Create a temporary link element to trigger download
	const link = document.createElement("a");
	link.href = templateUrl;
	link.download = "template.csv"; // You can set the downloaded filename here
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

async function uploadTemplate() {
	if (!fileBlob.value) {
		toast?.error("Please select a file before uploading");
		return;
	}

	const toastId = toast?.loading("Uploading shipment data...");

	try {
		// Create FormData object
		const formData = new FormData();

		// Add the file to the FormData with the expected field name 'shipmentFile'
		formData.append(
			"shipmentFile",
			fileBlob.value,
			fileBlob.value.name || "shipment.csv"
		);

		// Make the fetch request to upload the file
		const response = await fetch(
			`${import.meta.env["PUBLIC_API_URL"]}/shipments/upload-csv`,
			{
				method: "POST",
				headers: {
					Authorization: `Bearer ${localStorage.getItem(
						"auth_token"
					)}`,
					// Note: Don't set Content-Type header when using FormData
					// The browser will automatically set it with the correct boundary
				},
				body: formData,
			}
		);

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || "Failed to upload file");
		}

		// Parse the response
		const data = await response.json();

		toast?.update(toastId, {
			render: "Shipment file uploaded successfully! ",
			type: "success",
			isLoading: false,
			autoClose: 2000,
		});
	} catch (error) {
		console.error("Error uploading file:", error);

		// Update the toast with error message
		toast?.update(toastId, {
			render: `Upload failed: ${error.message}`,
			type: "error",
			isLoading: false,
			autoClose: 3000,
		});
	} finally {
		// Close the action modal
		showActionModal.value = false;
	}
}

async function fetchShipmentData(uuid) {
	try {
		const toastId = toast?.loading("Loading shipment data...");

		// Make API call to fetch the shipment data
		const response = await fetch(
			`${import.meta.env["PUBLIC_API_URL"]}/shipments/${uuid}`,
			{
				method: "GET",
				headers: {
					Authorization: `Bearer ${localStorage.getItem(
						"auth_token"
					)}`,
				},
			}
		);

		if (!response.ok) {
			toast?.update(toastId, {
				render: "Failed to load shipment data",
				type: "error",
				isLoading: false,
				autoClose: 2000,
			});
			return false;
		}

		let data = await response.json();

		if ("shipment" in data) {
			data = data["shipment"];
		}

		if (data.progress) {
			progress.value = isNaN(parseInt(data.progress))
				? 2
				: parseInt(data.progress) % 4;
			if (progress.value === 0) {
				progress.value = 4;
			}
		}

		if (progress.value === 1) {
			proNumber.value = data["proNumber"];
		}

		// Update the form data with the fetched data
		if (data.shipment_type_info) {
			formData.shipment_type_info = {
				...formData.shipment_type_info,
				...data.shipment_type_info,
			};
		}

		if (data.shipper_info?.shipper) {
			formData.shipper = {
				...formData.shipper,
				...data.shipper_info.shipper,
			};
		}

		if (data.shipper_info?.origin) {
			formData.origin = {
				...formData.origin,
				...data.shipper_info.origin,
			};
		}

		if (data.shipper_info?.destination) {
			formData.destination = {
				...formData.destination,
				...data.shipper_info.destination,
			};
		}

		if (data.line_items && Array.isArray(data.line_items)) {
			formData.line_items = [...data.line_items];
			// Update lineItemsQuantity based on fetched data
			lineItemsQuantity.value = data.line_items.length || 1;

			// Ensure all line items have sequential piece numbers if not already set
			formData.line_items.forEach((item, idx) => {
				if (!item.itemName) {
					item.itemName = String(idx + 1);
				}
			});
		}

		if (data.ocean_shipment) {
			formData.oceanShipment = {
				...formData.oceanShipment,
				...data.ocean_shipment,
			};
		}

		if (data?.status === "completed") {
			isCompleted.value = true;
		}

		// If data includes a file URL, set it
		if (data.fileUrl) {
			fileSrc.setFileSrc(data.fileUrl);
		}

		if (!isCompleted.value) {
			toast?.update(toastId, {
				render: "Shipment data loaded successfully",
				type: "success",
				isLoading: false,
				autoClose: 2000,
			});
		} else {
			toast.remove(toastId);
		}

		return true;
	} catch (error) {
		console.error("Error fetching shipment data:", error);
		toast?.error("Failed to load shipment data. Please try again.");
		return false;
	}
}

// Add this function with your other API functions
async function fetchServiceTypes() {
	try {
		isLoadingServiceTypes.value = true;
		const response = await fetch(
			`${
				import.meta.env["PUBLIC_API_URL"]
			}/admin/service-types?active=true&type=consumer`,
			{
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${localStorage.getItem(
						"auth_token"
					)}`,
				},
			}
		);

		if (response.ok) {
			const data = await response.json();
			serviceTypes.value = data.serviceTypes || [];
			console.log("Service types loaded:", serviceTypes.value);
		} else {
			console.error("Failed to fetch service types");
		}
	} catch (error) {
		console.error("Error fetching service types:", error);
	} finally {
		isLoadingServiceTypes.value = false;
	}
}

// Add this function with your other API functions
async function fetchContainerTypes() {
	try {
		isLoadingContainerTypes.value = true;
		const response = await fetch(
			`${
				import.meta.env["PUBLIC_API_URL"]
			}/admin/container-types?active=true`,
			{
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${localStorage.getItem(
						"auth_token"
					)}`,
				},
			}
		);

		if (response.ok) {
			const data = await response.json();
			containerTypes.value = data.containerTypes || [];
			console.log("Container types loaded:", containerTypes.value);
		} else {
			console.error("Failed to fetch container types");
		}
	} catch (error) {
		console.error("Error fetching container types:", error);
	} finally {
		isLoadingContainerTypes.value = false;
	}
}

// Add watcher for route changes
watch(
	() => route.params.uuid,
	async (newUuid) => {
		if (newUuid) {
			// Route changed to include a UUID
			isEditMode.value = true;
			shipmentUuid.value = newUuid;

			// Reset loading state
			isLoading.value = true;

			// Fetch data for the new UUID
			await fetchShipmentData(newUuid);

			isLoading.value = false;

			// If we're on step 2, initialize the datepickers
			if (progress.value === 2) {
				await nextTick();
				initDatepickers();

				const datePickerIcons = [
					document.querySelector("#spdsvg"),
					document.querySelector("#oecpsvg"),
					document.querySelector("#decpsvg"),
					document.querySelector("#srddsvg"),
				];

				datePickerIcons.forEach((icon) => {
					if (icon) {
						icon.addEventListener("click", () => {
							// Find the input field following this icon and click it
							icon.parentElement.lastElementChild.click();
						});
					}
				});
			}
		}
	},
	{ immediate: true }
);

const fileSrc = reactive({
	value: null,
	setFileSrc(src) {
		this.value = src;
	},
});

// Create refs for flatpickr date pickers
const pickupDatePickerEl = ref(null);
const pickupDatePicker = ref(null);
const rddDatePickerEl = ref(null);
const rddDatePicker = ref(null);
const etaOriginDatePickerEl = ref(null);
const etaOriginDatePicker = ref(null);
const etaDestDatePickerEl = ref(null);
const etaDestDatePicker = ref(null);

// Create reactive form data object
const formData = reactive({
	// Step 1 - Basic Shipment Info
	shipment_type_info: {
		shipmentType: "",
		gbl: "",
		registrationNumber: "",
		tsp: "",
		originGbloc: "",
		destGbloc: "",
		instructions: "",
	},

	// Step 2 - Shipper/Origin/Destination
	shipper: {
		firstName: "",
		lastName: "",
		pickupDate: "",
		rdd: "",
	},
	origin: {
		country: "",
		state: "",
		city: "",
		zip: "",
		portAgent: "",
		etaToPort: "",
	},
	destination: {
		country: "",
		state: "",
		city: "",
		zip: "",
		portAgent: "",
		etaToPort: "",
	},

	// Step 3 - Line Items
	line_items: [],

	// Step 4 - Ocean Shipment
	oceanShipment: {
		obolNumber: "",
		containerNumber: "",
		steamshipLine: "",
		vesselName: "",
	},
});

// Add a ref for the quantity
const lineItemsQuantity = ref(1);

// Function to create new line item
// Function to create new line item
function createEmptyLineItem(index) {
	return {
		id: Date.now() + Math.random().toString(36).substring(2, 9), // Generate unique id
		itemName: String(index + 1), // Auto-populate with sequential number
		grossWeight: "",
		tareWeight: "",
		netWeight: "",
		width: "",
		packageType: "",
		length: "",
		height: "",
		cubes: "",
		weightUnit: "",
		dimensionUnit: "",
		firearm: false,
		lion: false,
		hazmat: false,
	};
}

function deleteLineItem(index) {
	const decreasedValue = lineItemsQuantity.value - 1;

	if (decreasedValue === 0) {
		// call toast saying atleast one line item must be present
		toast?.error("At least one line item must be present");
		return false;
	}

	// Remove the item at the given index
	formData.line_items.splice(index, 1);

	// Decrease the quantity by 1
	lineItemsQuantity.value = Math.max(
		1,
		parseInt(lineItemsQuantity.value) - 1
	);

	// Renumber all remaining items to ensure sequential numbering
	formData.line_items.forEach((item, idx) => {
		item.itemName = String(idx + 1);
	});

	// Display success message
	toast?.success(`Line item #${index + 1} deleted successfully`);
}

const lineItems = computed(() => formData.line_items);

// Add a watcher for lineItemsQuantity that calls the method
watch(
	() => lineItemsQuantity.value,
	() => {
		updateLineItems();
	},
	{ immediate: true }
);

function updateLineItems() {
	const qty = parseInt(lineItemsQuantity.value) || 0;

	// If formData already has line items, use them up to the quantity
	let existingItems = [];
	if (formData.line_items && formData.line_items.length > 0) {
		existingItems = formData.line_items.slice(0, qty);
	}

	// Add more items if needed
	const additionalItems = [];
	for (let i = existingItems.length; i < qty; i++) {
		additionalItems.push(createEmptyLineItem(i));
	}

	// Update formData.line_items
	formData.line_items = [...existingItems, ...additionalItems];

	return formData.line_items;
}

// Function to validate line items
function validateLineItems() {
	const qty = parseInt(lineItemsQuantity.value) || 0;

	if (qty <= 0) {
		toast?.error("Please enter a valid quantity");
		return false;
	}

	// Check if all line items have required fields
	for (let i = 0; i < formData.line_items.length; i++) {
		const item = formData.line_items[i];

		if (!item.itemName) {
			toast?.error(`Line item #${i + 1} missing name`);
			return false;
		}
	}

	return true;
}

// Function to reset the file input
function resetFile() {
	fileBlob.value = null;
	if (fileSrc.value && fileSrc.value.startsWith("blob")) {
		URL.revokeObjectURL(fileSrc.value);
	}
	fileSrc.setFileSrc(null);
	if (fileInputTag.value) {
		fileInputTag.value.value = "";
	}
}

// Function to handle file change
async function fileChange(evt) {
	const file = evt.target.files[0];

	if (fileSrc) {
		resetFile();
	}

	if (file) {
		if (MIMES.indexOf(file.type) === -1) {
			toast?.error(
				"Sorry currently only CSV (Comma Separated Value) files are accepted."
			);
			fileInputTag.value.value = "";
			return;
		}

		if (file.size > 25 * 1024 * 1024) {
			toast?.error(
				"File size exceeds 25MB limit. Please upload a smaller file."
			);
			fileInputTag.value.value = "";
			return;
		}

		fileBlob.value = new Blob([file], {
			type: file.type,
		});

		fileSrc.setFileSrc(URL.createObjectURL(fileBlob.value));
	}
}

onMounted(async () => {
	// Check authentication before loading the page
	const token = localStorage.getItem("auth_token");
	const expiresAt = localStorage.getItem("expiresAt");

	// If no token or token is expired, redirect to login
	if (
		!token ||
		!expiresAt ||
		new Date(expiresAt).getTime() <= new Date().getTime()
	) {
		localStorage.clear();
		router.push("/login");
		return;
	}

	// Route watcher handles the UUID check and data loading now
	// This just finalizes the loading state if we don't have a UUID
	if (!route.params.uuid) {
		isLoading.value = false;
	}
});

// Helper function to initialize all datepickers
function initDatepickers() {
	console.log("Initializing datepickers");

	// Initialize datepickers when on page 2
	if (pickupDatePickerEl.value) {
		if (pickupDatePicker.value) {
			pickupDatePicker.value.destroy();
		}

		pickupDatePicker.value = flatpickr(pickupDatePickerEl.value, {
			altInput: true,
			altFormat: "F j, Y",
			dateFormat: "Y-m-d",
			defaultDate: formData.shipper.pickupDate || null,
			onChange: (selectedDates, dateStr) => {
				formData.shipper.pickupDate = dateStr;
				console.log("Pickup date selected:", dateStr);
			},
		});
	}

	if (rddDatePickerEl.value) {
		if (rddDatePicker.value) {
			rddDatePicker.value.destroy();
		}

		rddDatePicker.value = flatpickr(rddDatePickerEl.value, {
			altInput: true,
			altFormat: "F j, Y",
			dateFormat: "Y-m-d",
			minDate: "today",
			defaultDate: formData.shipper.rdd || null,
			onChange: (selectedDates, dateStr) => {
				formData.shipper.rdd = dateStr;
				console.log("RDD date selected:", dateStr);
			},
		});
	}

	if (etaOriginDatePickerEl.value) {
		if (etaOriginDatePicker.value) {
			etaOriginDatePicker.value.destroy();
		}

		etaOriginDatePicker.value = flatpickr(etaOriginDatePickerEl.value, {
			altInput: true,
			altFormat: "F j, Y",
			dateFormat: "Y-m-d",
			minDate: "today",
			defaultDate: formData.origin.etaToPort || null,
			onChange: (selectedDates, dateStr) => {
				formData.origin.etaToPort = dateStr;
				console.log("Origin ETA date selected:", dateStr);
			},
		});
	}

	if (etaDestDatePickerEl.value) {
		if (etaDestDatePicker.value) {
			etaDestDatePicker.value.destroy();
		}

		etaDestDatePicker.value = flatpickr(etaDestDatePickerEl.value, {
			altInput: true,
			altFormat: "F j, Y",
			dateFormat: "Y-m-d",
			minDate: "today",
			defaultDate: formData.destination.etaToPort || null,
			onChange: (selectedDates, dateStr) => {
				formData.destination.etaToPort = dateStr;
				console.log("Destination ETA date selected:", dateStr);
			},
		});
	}
}

// Functions for navigation between form steps
async function goToNextStep() {
	// For step 1, use the special submission logic
	if (progress.value === 1) {
		if (isCompleted.value) {
			progress.value++;
		} else if (isEditMode.value) {
			// In edit mode, update the existing record
			await updateStep1();
		} else {
			// In create mode, create a new record
			await submitStep1();
		}
		return;
	}

	if (!isCompleted.value) {
		// For steps 2, 3, and 4
		if (validateCurrentStep()) {
			// First save the current step data
			const success = await updateStepData(progress.value);

			if (success) {
				// Only advance to the next step if data was saved successfully
				progress.value++;
				toast?.success(`Step ${progress.value - 1} completed!`);
			}
		}
	} else {
		progress.value++;
	}
}

function goToPreviousStep() {
	if (progress.value > 1) {
		progress.value--;
	}
}

// Modify validateCurrentStep to include line items validation
function validateCurrentStep() {
	// Add validation logic specific to each step
	switch (progress.value) {
		case 1:
			// Example validation for step 1
			if (!formData.shipment_type_info.shipmentType) {
				toast?.error("Please select a shipment type");
				return false;
			}
			// if (!formData.shipment_type_info.gbl) {
			// 	toast?.error("Please enter a GBL number");
			// 	return false;
			// }
			if (!formData.shipment_type_info.registrationNumber) {
				toast?.error("Please enter your registration number");
				return false;
			}
			if (!formData.shipment_type_info.tsp) {
				toast?.error("Please enter the Carrier Name & SCAC");
				return false;
			}

			return true; // Return true when all validations pass

		case 2:
			// Example validation for step 2
			if (!formData.shipper.firstName || !formData.shipper.lastName) {
				toast?.error("Shipper first name & last name is required");
				return false;
			}

			if (!formData.origin.country) {
				toast?.error("Origin country is required");
				return false;
			}

			let isUS =
				formData.origin.country === "United States" ||
				formData.origin.country === "US";
			if (isUS && !formData.origin.state) {
				toast?.error("Origin state is required");
				return false;
			}

			if (isUS && !formData.origin.city) {
				toast?.error("Origin city is required");
				return false;
			}

			if (isUS && !formData.origin.zip) {
				toast?.error("Origin zip code is required");
				return false;
			}

			if (!formData.origin.portAgent) {
				toast?.error("Origin port & agent information is required");
				return false;
			}

			if (!formData.origin.country) {
				toast?.error("Origin country is required");
				return false;
			}

			isUS =
				formData.destination.country === "United States" ||
				formData.destination.country === "US";
			if (isUS && !formData.destination.state) {
				toast?.error("Destination state is required");
				return false;
			}

			if (isUS && !formData.destination.city) {
				toast?.error("Destination city is required");
				return false;
			}

			if (isUS && !formData.destination.zip) {
				toast?.error("Destination zip code is required");
				return false;
			}

			if (!formData.destination.portAgent) {
				toast?.error(
					"Destination port & agent information is required"
				);
				return false;
			}

			return true;

		case 3:
			// Validation for step 3 (line items)
			return validateLineItems();

		case 4:
		// Existing validation...

		default:
			return true;
	}
}

// Function to submit Step 1 data (for new shipments)
async function submitStep1() {
	try {
		if (!validateCurrentStep()) {
			return false; // Stop if validation fails
		}

		// Show loading toast
		const toastId = toast?.loading(
			"Please wait while we process your request!"
		);

		// Prepare data for the backend - just step 1 data
		const stepOneData = {
			type: "consumer",
			shipment_type_info: formData.shipment_type_info,
			progress: 2,
		};

		console.log("Submitting Step 1 data:", stepOneData);

		// Make API call to create a new shipment
		const response = await fetch(
			`${import.meta.env["PUBLIC_API_URL"]}/shipments`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${localStorage.getItem(
						"auth_token"
					)}`,
				},
				body: JSON.stringify(stepOneData),
			}
		);

		if (!response.ok) {
			toast?.update(toastId, {
				render: "Failed to create shipment record",
				type: "error",
				isLoading: false,
				autoClose: 2000,
			});

			return false;
		}

		let data = await response.json();
		data = data["shipment"];
		const uuid = data.id;

		// Update toast on success
		toast?.update(toastId, {
			render: "Shipment record created successfully!",
			type: "success",
			isLoading: false,
			autoClose: 2000,
		});

		// Instead of setting progress directly, let route watcher handle it
		// after navigation
		setTimeout(() => {
			router.replace(`/consumer-shipment/${uuid}`);
		}, 2000);
	} catch (error) {
		console.error("Error creating shipment record:", error);
		toast?.error("Failed to create shipment record. Please try again.");
	}
}

// Function to update Step 1 data (for existing shipments)
async function updateStep1() {
	try {
		if (!validateCurrentStep()) {
			return false; // Stop if validation fails
		}

		// Show loading toast
		const toastId = toast?.loading("Updating shipment information...");

		// Prepare data for the backend - just step 1 data
		const stepOneData = {
			shipment_type_info: formData.shipment_type_info,
			progress: 2,
		};

		console.log("Updating Step 1 data for shipment:", shipmentUuid.value);

		// Make API call to update the existing shipment

		const response = await fetch(
			`${import.meta.env["PUBLIC_API_URL"]}/shipments/${
				shipmentUuid.value
			}`,
			{
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${localStorage.getItem(
						"auth_token"
					)}`,
				},
				body: JSON.stringify(stepOneData),
			}
		);

		if (!response.ok) {
			toast?.update(toastId, {
				render: "Failed to update shipment record",
				type: "error",
				isLoading: false,
				autoClose: 2000,
			});

			return false;
		}

		// Update toast on success
		toast?.update(toastId, {
			render: "Shipment information updated successfully!",
			type: "success",
			isLoading: false,
			autoClose: 2000,
		});

		// Advance to next step without redirect
		progress.value++;
	} catch (error) {
		console.error("Error updating shipment record:", error);
		toast?.error(
			"Failed to update shipment information. Please try again."
		);
	}
}

// Function to update data for specific steps (2,3,4)
async function updateStepData(step) {
	try {
		if (!validateCurrentStep()) {
			return false;
		}

		const toastId = toast?.loading("Updating shipment information...");

		// Prepare data based on step
		let stepData = {
			progress: step + 1, // Send the next step as progress
		};

		switch (step) {
			case 2:
				stepData = {
					...stepData,
					shipper_info: {
						shipper: formData.shipper,
						origin: formData.origin,
						destination: formData.destination,
					},
				};
				break;
			case 3:
				stepData = {
					...stepData,
					line_items: formData.line_items,
				};
				break;
			case 4:
				stepData = {
					...stepData,
					ocean_shipment: formData.oceanShipment,
					progress: 5,
					status: "completed",
				};
				break;
		}

		console.log(
			`Updating Step ${step} data for shipment:`,
			shipmentUuid.value
		);
		console.log("Request payload:", JSON.stringify(stepData));

		// Make API call to update the shipment
		const response = await fetch(
			`${import.meta.env["PUBLIC_API_URL"]}/shipments/${
				shipmentUuid.value
			}`,
			{
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${localStorage.getItem(
						"auth_token"
					)}`,
				},
				body: JSON.stringify(stepData),
			}
		);

		if (!response.ok) {
			toast?.update(toastId, {
				render: "Failed to update shipment information",
				type: "error",
				isLoading: false,
				autoClose: 2000,
			});

			return false;
		}

		toast?.update(toastId, {
			render: "Shipment information updated successfully!",
			type: "success",
			isLoading: false,
			autoClose: 2000,
		});

		return true;
	} catch (error) {
		console.error("Error updating shipment information:", error);
		toast?.error(
			"Failed to update shipment information. Please try again."
		);
		return false;
	}
}

// Watch for changes in progress for edit mode
onMounted(async () => {
	// Check authentication before loading the page
	const token = localStorage.getItem("auth_token");
	const expiresAt = localStorage.getItem("expiresAt");

	// If no token or token is expired, redirect to login
	if (
		!token ||
		!expiresAt ||
		new Date(expiresAt).getTime() <= new Date().getTime()
	) {
		toast?.error("Authentication required");
		router.push("/login");
		return;
	}

	// Route watcher handles the UUID check and data loading now
	// This just finalizes the loading state if we don't have a UUID
	if (!route.params.uuid) {
		isLoading.value = false;
	}

	// Set up a watcher to initialize datepickers when progress changes to step 2
});

watch(
	() => progress.value,
	async (newVal) => {
		if (newVal === 2) {
			await nextTick();
			initDatepickers();

			const datePickerIcons = [
				document.querySelector("#spdsvg"),
				document.querySelector("#oecpsvg"),
				document.querySelector("#decpsvg"),
				document.querySelector("#srddsvg"),
			];

			datePickerIcons.forEach((icon) => {
				if (icon) {
					icon.addEventListener("click", () => {
						// Find the input field following this icon and click it
						icon.parentElement.lastElementChild.click();
					});
				}
			});
		}
	}
);

// Handle final form submission
async function submitForm() {
	try {
		// Validate the final step
		if (!validateCurrentStep()) {
			return;
		}

		// Show loading toast
		const toastId = toast?.loading("Finalizing shipment registration...");

		// In edit mode, just update the current step data
		if (isEditMode.value) {
			const success = await updateStepData(progress.value);

			if (success) {
				toast?.update(toastId, {
					render: "Shipment registration completed successfully!",
					type: "success",
					isLoading: false,
					autoClose: 2000,
				});

				// Redirect to shipment view page or dashboard
				setTimeout(() => {
					openRegistrationCompleteModal();
				}, 2000);
			}
		} else {	
			// This is a safeguard but should never happen in practice
			toast?.update(toastId, {
				render: "Invalid operation. Please try again.",
				type: "error",
				isLoading: false,
				autoClose: 2000,
			});
		}
	} catch (error) {
		console.error("Error finalizing shipment:", error);
		toast?.error(
			"Failed to complete shipment registration. Please try again."
		);
	}
}

// Function to check if we're dealing with a PDF file
function isPdfFile() {
	return (
		fileSrc.value &&
		fileBlob.value &&
		fileBlob.value.type.indexOf("pdf") !== -1
	);
}

function goToShipments() {
	router.go("/");
}

watch(
	() => formData.line_items,
	(newItems) => {
		// For each line item, calculate net weight when gross or tare weight changes
		newItems.forEach((item) => {
			const grossWeight = parseFloat(item.grossWeight) || 0;
			const tareWeight = parseFloat(item.tareWeight) || 0;

			// Update net weight (gross - tare)
			item.netWeight = Math.max(0, grossWeight - tareWeight).toFixed(2);

			// Calculate cube value based on dimensions and selected unit
			const length = parseFloat(item.length) || 0;
			const width = parseFloat(item.width) || 0;
			const height = parseFloat(item.height) || 0;

			if (length > 0 && width > 0 && height > 0) {
				if (item.dimensionUnit === "cm") {
					// Convert cubic cm to cubic meters: (L × W × H) ÷ 10,000 = cubic meters
					const cubicMeters = (length * width * height) / 10000;
					item.cubes = cubicMeters.toFixed(2);
				} else {
					// Default is inches - convert cubic inches to cubic feet: (L × W × H) ÷ 1728 = cubic feet
					const cubicFeet = (length * width * height) / 1728;
					item.cubes = cubicFeet.toFixed(2);
				}
			} else {
				item.cubes = "0.00";
			}
		});
	},
	{ deep: true }
);

let USIndex;
let CountryList = Country.getAllCountries().map((item, index) => {
	if (item.isoCode === "US") USIndex = index;
	return {
		name: item.name,
		isoCode: item.isoCode,
	};
});

if (USIndex) {
	let tmp = CountryList[USIndex];
	CountryList.splice(USIndex, 1);
	CountryList = [tmp, ...CountryList];
}

const originStates = ref([]);
const originCity = ref([]);

const destinationStates = ref([]);
const destinationCity = ref([]);

const isUSOnlyOrigin = ref(false);
const isUSOnlyDestination = ref(false);

const originCountryInp = useTemplateRef("ooc");
const originStateInp = useTemplateRef("oos");
const originCityInp = useTemplateRef("ooy");
const originPortAgentInp = useTemplateRef("opa");

const ddcCountryInp = useTemplateRef("ddc");
const ddcStateInp = useTemplateRef("dds");
const ddcCityInp = useTemplateRef("ddy");
const ddcPortAgentInp = useTemplateRef("dpa");

// Watch for changes to origin country
watch(
	() => formData.origin.country,
	async (newCountry) => {
		isUSOnlyOrigin.value =
			newCountry === "United States" || newCountry === "US";
		const countryObj = CountryList.find(
			(c) => c.isoCode === newCountry || c.name === newCountry
		);
		if (countryObj) {
			// Get states for the selected country using its ISO code

			ooc.blur();

			originStates.value = State.getStatesOfCountry(countryObj.isoCode);
		} else {
			// Reset states list if country is not found
			originStates.value = [];
		}

		// Add port agents fetch by country ISO code
		await fetchPortAgentsByCountry(newCountry, true);
	}
);

// Update the origin state watcher
watch(
	() => formData.origin.state,
	(newState) => {
		console.log({ newState });

		const stateObj = originStates.value.find(
			(s) => s.isoCode === newState || s.name === newState
		);

		console.log(originStates.value, stateObj);

		const countryObj = CountryList.find(
			(c) =>
				c.isoCode === formData.origin.country ||
				c.name === formData.origin.country
		);

		if (stateObj && countryObj) {
			// Store ISO code in formData

			oos.blur();

			formData.origin.state = isUSOnlyOrigin.value
				? stateObj.isoCode
				: stateObj.name;

			// Get cities for the selected state and country
			originCity.value = City.getCitiesOfState(
				countryObj.isoCode,
				stateObj.isoCode
			);
		} else {
			// Reset cities list if state is not found
			originCity.value = [];
		}
	}
);

watch(
	() => formData.origin.city,
	(newCity) => {
		const stateObj = originStates.value.find(
			(s) =>
				s.isoCode === formData.origin.state ||
				s.name === formData.origin.state
		);

		// console.log(originStates.value, stateObj);

		const countryObj = CountryList.find(
			(c) =>
				c.isoCode === formData.origin.country ||
				c.name === formData.origin.country
		);

		const CityList = City.getCitiesOfState(
			countryObj.isoCode,
			stateObj.isoCode
		);

		const cityObj = CityList.find(
			(c) => c.isoCode === newCity || c.name === newCity
		);

		if (cityObj) {
			ooy.blur();
		}
	}
);

watch(
	() => formData.origin.portAgent,
	(newPort) => {
		const p = originPortAgents.value.find(
			(agent) => agent.port_name + " / " + agent.agent_name === newPort
		);

		// console.log()

		if (p) {
			opa.blur();
		}
	}
);

watch(
	() => formData.destination.country,
	async (newCountry) => {
		console.log({ newCountry });
		// Find the country object from our CountryList
		const countryObj = CountryList.find(
			(c) => c.isoCode === newCountry || c.name === newCountry
		);
		isUSOnlyDestination.value =
			newCountry === "United States" || newCountry === "US";
		if (countryObj) {
			ddc.blur();
			// Get states for the selected country using its ISO code
			destinationStates.value = State.getStatesOfCountry(
				countryObj.isoCode
			);
		} else {
			// Reset states list if country is not found
			destinationStates.value = [];
		}

		// Add port agents fetch by country ISO code
		await fetchPortAgentsByCountry(newCountry, false);
	}
);

watch(
	() => formData.destination.state,
	(newState) => {
		console.log({ newState });

		const stateObj = destinationStates.value.find(
			(s) => s.isoCode === newState || s.name === newState
		);

		console.log(destinationStates.value, stateObj);

		const countryObj = CountryList.find(
			(c) =>
				c.isoCode === formData.destination.country ||
				c.name === formData.destination.country
		);

		if (stateObj && countryObj) {
			// Store ISO code in formData
			dds.blur();
			formData.destination.state = isUSOnlyDestination.value
				? stateObj.isoCode
				: stateObj.name;

			// Get cities for the selected state and country
			destinationCity.value = City.getCitiesOfState(
				countryObj.isoCode,
				stateObj.isoCode
			);
		} else {
			// Reset cities list if state is not found
			destinationCity.value = [];
		}
	}
);

watch(
	() => formData.destination.city,
	(newCity) => {
		const stateObj = destinationStates.value.find(
			(s) =>
				s.isoCode === formData.destination.state ||
				s.name === formData.destination.state
		);

		// console.log(originStates.value, stateObj);

		const countryObj = CountryList.find(
			(c) =>
				c.isoCode === formData.destination.country ||
				c.name === formData.destination.country
		);

		const CityList = City.getCitiesOfState(
			countryObj.isoCode,
			stateObj.isoCode
		);

		const cityObj = CityList.find(
			(c) => c.isoCode === newCity || c.name === newCity
		);

		if (cityObj) {
			ddy.blur();
		}
	}
);

watch(
	() => formData.destination.portAgent,
	(newPort) => {
		const p = destinationPortAgents.value.find(
			(agent) => agent.port_name + " / " + agent.agent_name === newPort
		);

		// console.log()

		if (p) {
			dpa.blur();
		}
	}
);

// Function to fetch port agents based on user ID only
async function fetchUserPortAgents() {
	try {
		// For origin port agents
		isLoadingOriginPortAgents.value = true;
		const response = await fetch(
			`${
				import.meta.env["PUBLIC_API_URL"]
			}/admin/port-agents?active=true`,
			{
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${localStorage.getItem(
						"auth_token"
					)}`,
				},
			}
		);

		if (response.ok) {
			const data = await response.json();
			originPortAgents.value = data.portAgents || [];
			// Initially use the same list for destination
			destinationPortAgents.value = [...originPortAgents.value];
		}
	} catch (error) {
		console.error("Error fetching port agents:", error);
	} finally {
		isLoadingOriginPortAgents.value = false;
	}
}

// Function to fetch port agents by country
async function fetchPortAgentsByCountry(country, isOrigin = true) {
	if (!country) return;

	try {
		if (isOrigin) {
			isLoadingOriginPortAgents.value = true;
		} else {
			isLoadingDestinationPortAgents.value = true;
		}

		// Only make API call for US, otherwise use default list
		if (country === "US" || country === "United States") {
			const response = await fetch(
				`${
					import.meta.env["PUBLIC_API_URL"]
				}/admin/port-agents/?country=${encodeURIComponent(
					"United States"
				)}&active=true`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${localStorage.getItem(
							"auth_token"
						)}`,
					},
				}
			);

			if (response.ok) {
				const data = await response.json();
				if (isOrigin) {
					originPortAgents.value = data.portAgents || [];
				} else {
					destinationPortAgents.value = data.portAgents || [];
				}
			}
		}
		// For non-US countries, keep using the default list from fetchUserPortAgents
	} catch (error) {
		console.error(
			`Error fetching ${
				isOrigin ? "origin" : "destination"
			} port agents for ${country}:`,
			error
		);
	} finally {
		if (isOrigin) {
			isLoadingOriginPortAgents.value = false;
		} else {
			isLoadingDestinationPortAgents.value = false;
		}
	}

	console.log({
		opa: originPortAgents.value,
	});
}

// Modify onMounted to fetch port agents on initial load
onMounted(async () => {
	// Check authentication before loading the page
	const token = localStorage.getItem("auth_token");
	const expiresAt = localStorage.getItem("expiresAt");

	// If no token or token is expired, redirect to login
	if (
		!token ||
		!expiresAt ||
		new Date(expiresAt).getTime() <= new Date().getTime()
	) {
		toast?.error("Authentication required");
		router.push("/login");
		return;
	}

	// Fetch service types on component mount
	await fetchServiceTypes();

	// Fetch container types on component mount
	await fetchContainerTypes();

	// Fetch port agents on component mount
	await fetchUserPortAgents();

	// Route watcher handles the UUID check and data loading now
	// This just finalizes the loading state if we don't have a UUID
	if (!route.params.uuid) {
		isLoading.value = false;
	}
});

// Add this function to toggle the modal visibility
function toggleModal(index = 0) {
	selectedLineItemIndex.value = index;
	isModalOpen.value = !isModalOpen.value;
}

// Add the formatDate and formatLocation functions to handle label data
function formatDate(dateString) {
	if (!dateString) return "N/A";
	const date = new Date(dateString);
	if (isNaN(date.getTime())) return "N/A";

	// Format as DD MMM YYYY (e.g., 01 JAN 2025)
	const day = String(date.getDate()).padStart(2, "0");
	const month = date
		.toLocaleString("en-US", { month: "short" })
		.toUpperCase();
	const year = date.getFullYear();

	return `${day} ${month} ${year}`;
}

function formatLocation(location) {
	if (!location) return "N/A";

	const parts = [];
	if (location.city) parts.push(location.city);
	if (location.state) parts.push(location.state);
	if (location.country === "United States" || location.country === "US") {
		parts.push("US");
	} else if (location.country) {
		parts.push(location.country);
	}

	return parts.join(" ");
}

// Calculate total weights from line items
function calculateTotalWeight(type) {
	if (!formData.line_items || !formData.line_items.length) return "0";

	let total = 0;

	const selectedItem = formData.line_items[selectedLineItemIndex.value];

	if (type === "gross" && selectedItem.grossWeight) {
		total += parseFloat(selectedItem.grossWeight) || 0;
	} else if (type === "tare" && selectedItem.tareWeight) {
		total += parseFloat(selectedItem.tareWeight) || 0;
	} else if (type === "net" && selectedItem.netWeight) {
		total += parseFloat(selectedItem.netWeight) || 0;
	}

	// Format with commas for thousands
	return total.toString();
}

function calculateTotalCube() {
	if (!formData.line_items || !formData.line_items.length) return "0";

	let total = 0;

	const selectedItem = formData.line_items[selectedLineItemIndex.value];

	return parseFloat(selectedItem.cubes).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

function calculateLineItem() {
	if (!formData.line_items || !formData.line_items.length) return "0";

	let total = 0;

	const selectedItem = formData.line_items[selectedLineItemIndex.value];

	return `${selectedItem.itemName} OF ${formData.line_items.length}`;
}

// Function to print the shipping label
function printShippingLabel() {
	const printWindow = window.open("", "_blank");

	// Create a complete HTML document for printing

	printWindow.document.write(`<html><head><style>@page {
				size: letter landscape;
			}

			@media print {
				@page {
					size: letter landscape;
				}
			}
			
			body {
				font-family: Arial, sans-serif;
				margin: 0;
				padding: 0;
				background-color: #fff;
				display: flex;
				max-width: 99% !important;
				height: 99%;
				scale: 99%;
			}
			
			</style>${document.querySelector(".shipping-label-container").innerHTML}`);

	setTimeout(() => {
		printWindow.print();
	}, 1000);
}

// When opening the modal, set up barcode and data matrix
watch(
	() => isModalOpen.value,
	(newVal) => {
		if (newVal) {
			// Give DOM time to render
			nextTick(() => {
				try {
					// Load and initialize external scripts if needed
					loadExternalScripts().then(() => {
						renderBarcodes();
					});
				} catch (err) {
					console.error("Error rendering barcodes:", err);
				}
			});
		}
	}
);

// Function to load external scripts if they're not already loaded
function loadExternalScripts() {
	return new Promise((resolve, reject) => {
		// Check if scripts are already loaded
		if (window.DATAMatrix && window.JsBarcode) {
			resolve();
			return;
		}

		const loadDataMatrix = new Promise((res, rej) => {
			const script = document.createElement("script");
			script.src =
				"https://datalog.github.io/demo/datamatrix-svg/datamatrix.min.js";
			script.onload = () => res();
			script.onerror = () =>
				rej(new Error("Failed to load DataMatrix script"));
			document.head.appendChild(script);
		});

		const loadJsBarcode = new Promise((res, rej) => {
			const script = document.createElement("script");
			script.src =
				"https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js";
			script.onload = () => res();
			script.onerror = () =>
				rej(new Error("Failed to load JsBarcode script"));
			document.head.appendChild(script);
		});

		Promise.all([loadDataMatrix, loadJsBarcode])
			.then(resolve)
			.catch(reject);
	});
}

// Function to render barcodes once scripts are loaded
function renderBarcodes() {
	if (!window.DATAMatrix || !window.JsBarcode) {
		console.error("Required libraries not loaded");
		return;
	}

	// Get the selected line item
	const selectedItem = formData.line_items[selectedLineItemIndex.value];

	// Create data for the data matrix
	const shipmentData = `<Shipment>
    <FullName>${formData.shipper.lastName}, ${
		formData.shipper.firstName
	}</FullName>
    <FirstName>${formData.shipper.firstName}</FirstName>
    <LastName>${formData.shipper.lastName}</LastName>
    <SCAC>${formData.shipment_type_info.tsp}</SCAC>
    <Reference>${formData.shipment_type_info.registrationNumber}</Reference>
    <Item>${selectedItem.itemName}</Item>
    <PackageType>${selectedItem.packageType || ""}</PackageType>
    <PickupDate>${formatDate(formData.shipper.pickupDate)}</PickupDate>
    <RDD>${formatDate(formData.shipper.rdd)}</RDD>
    <PickupAdress>${formatLocation(formData.origin)}</PickupAdress>
    <DeliveryAdress>${formatLocation(formData.destination)}</DeliveryAdress>
    <OriginGBLOC></OriginGBLOC>
    <DestinationGBLOC></DestinationGBLOC>
    <GrossWeight>${selectedItem.grossWeight || "0"}</GrossWeight>
    <TareWeight>${selectedItem.tareWeight || "0"}</TareWeight>
    <NetWt>${selectedItem.netWeight || "0"}</NetWt>
    <Cube>${selectedItem.cubes || "0"}</Cube>
    <ItemCount>${selectedLineItemIndex.value + 1} OF ${
		formData.line_items.length
	}</ItemCount>
    <TrackingNumber>${selectedItem?.barcode}</TrackingNumber>
  </Shipment>`;

	// Clear existing SVG node content first
	const svgNodeEl = document.getElementById("svg-node");
	if (svgNodeEl) {
		// Remove any existing data matrix
		const existingMatrix = svgNodeEl.querySelector("svg");
		if (existingMatrix) {
			existingMatrix.remove();
		}

		// Generate new data matrix
		const svgNode = window.DATAMatrix({
			msg: shipmentData,
			dim: 128,
		});
		svgNodeEl.appendChild(svgNode);
	}

	// Generate barcode
	const barcodeEl = document.getElementById("barcode");
	if (barcodeEl) {
		try {
			// Use the line item barcode if available, otherwise use GBL
			const barcodeValue = selectedItem?.barcode;
			window.JsBarcode("#barcode", barcodeValue, {
				width: 3,
			});
		} catch (err) {
			console.error("Error generating barcode:", err);
		}
	}
}

// Function to print all shipping labels
// Function to print all shipping labels with lock check and data refresh
async function printAllShippingLabels() {
    if (!shipmentUuid.value) {
        toast?.error("No shipment ID available");
        return;
    }

    // Show loading toast
    const toastId = toast?.loading("Checking shipment status and preparing labels...");

    try {
        // First, fetch the latest shipment data to check lock status and refresh data
        const response = await fetch(
            `${import.meta.env["PUBLIC_API_URL"]}/shipments/${shipmentUuid.value}`,
            {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
                },
            }
        );

        if (!response.ok) {
            throw new Error("Failed to fetch shipment data");
        }

        let data = await response.json();
        if ("shipment" in data) {
            data = data["shipment"];
        }

        // Check if shipment is locked
        if (data.locked || data.isLocked || data.status === "locked") {
            toast?.update(toastId, {
                render: "Cannot print labels: Shipment is currently locked",
                type: "error",
                isLoading: false,
                autoClose: 3000,
            });
            return;
        }

        // Update local formData with fresh data from API
        if (data.shipment_type_info) {
            formData.shipment_type_info = {
                ...formData.shipment_type_info,
                ...data.shipment_type_info,
            };
        }

        if (data.shipper_info?.shipper) {
            formData.shipper = {
                ...formData.shipper,
                ...data.shipper_info.shipper,
            };
        }

        if (data.shipper_info?.origin) {
            formData.origin = {
                ...formData.origin,
                ...data.shipper_info.origin,
            };
        }

        if (data.shipper_info?.destination) {
            formData.destination = {
                ...formData.destination,
                ...data.shipper_info.destination,
            };
        }

        if (data.line_items && Array.isArray(data.line_items)) {
            formData.line_items = [...data.line_items];
        }

        if (data.ocean_shipment) {
            formData.oceanShipment = {
                ...formData.oceanShipment,
                ...data.ocean_shipment,
            };
        }

        // Update proNumber if available
        if (data.proNumber) {
            proNumber.value = data.proNumber;
        }

        // Check if we have line items after refresh
        if (!formData.line_items || formData.line_items.length === 0) {
            toast?.update(toastId, {
                render: "No line items found to print",
                type: "error",
                isLoading: false,
                autoClose: 3000,
            });
            return;
        }

        // Update toast to show we're now generating labels
        toast?.update(toastId, {
            render: "Generating shipping labels...",
            type: "info",
            isLoading: true,
        });

        const printWindow = window.open("", "_blank");

        // Create the base HTML structure
        printWindow.document.write(`
            <html>
            <head>
                <title>Labels for #${proNumber.value}</title>
                <style>
                @page {
                    size: letter landscape;
                }

                @media print {
                    @page {
                        size: letter landscape;
                    }
                    
                    .page-break {
                    page-break-after: always;
                    }
                    
                    .page-break:last-child {
                    page-break-after: avoid;
                    }
                }
                
                body {
                    font-family: Arial, sans-serif !important;
                    margin: 0;
                    padding: 0;
                    display: flex;
                    flex-direction: column;
                    max-width: 99% !important;
                    height: 99%;
                    scale: 99%;
                    background-color: #fff;
                }

                .shipping-label {
                    width: 100%;
                    height: 100vh;
                    position: relative;
                    box-sizing: border-box;
                    max-height: 100vh;
                }

                .header {
                    line-height: 1;
                }

                .header h1 {
                    font-size: 60px;
                    font-family: Arial, sans-serif !important;
                    margin: 0;
                }

                .scac-container {
                    position: absolute;
                    right: 10px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                }

                .reg-numbers {
                    display: flex;
                    flex-direction: row;
                    gap: 90px;
                }

                .dates {
                    display: flex;
                    flex-direction: row;
                    gap: 20px;
                }

                .locations {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .weights {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                }

                .weight-item {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    align-items: center;
                }

                .barcode-section {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-around;
                    align-items: center;
                }

                .data-matrix-container {
                    display: flex;
                    flex-direction: column;
                }

                svg {
                    max-width: 100%;
                    height: auto;
                }
                </style>
            </head>
            <body>
        `);

        // Load external scripts
        await new Promise((resolve) => {
            // Load DataMatrix script
            const dataMatrixScript = printWindow.document.createElement("script");
            dataMatrixScript.src =
                "https://datalog.github.io/demo/datamatrix-svg/datamatrix.min.js";
            dataMatrixScript.onload = () => {
                // Load JsBarcode script
                const barcodeScript = printWindow.document.createElement("script");
                barcodeScript.src =
                    "https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js";
                barcodeScript.onload = resolve;
                printWindow.document.head.appendChild(barcodeScript);
            };
            printWindow.document.head.appendChild(dataMatrixScript);
        });

        // Generate each label
        for (let index = 0; index < formData.line_items.length; index++) {
            const item = formData.line_items[index];

            // Create unique IDs for this label
            const svgNodeId = `svg-node-${index}`;
            const barcodeId = `barcode-${index}`;

            // Generate the label HTML
            const labelHTML = generateLabelHTML(item, index, svgNodeId, barcodeId);

            // Add page break class to all but the last label
            const pageBreakClass =
                index < formData.line_items.length - 1 ? "page-break" : "";

            printWindow.document.write(`
            <div class="shipping-label ${pageBreakClass}">
                ${labelHTML}
            </div>
            `);
        }

        // Close the HTML structure
        printWindow.document.write(`
            </body>
            </html>
        `);

        // Update toast to show completion
        toast?.update(toastId, {
            render: "Labels generated successfully!",
            type: "success",
            isLoading: false,
            autoClose: 2000,
        });

        // Wait for everything to load, then generate barcodes and print
        setTimeout(() => {
            // Generate all barcodes and data matrices
            for (let index = 0; index < formData.line_items.length; index++) {
                const item = formData.line_items[index];
                generateBarcodesForLabel(printWindow, item, index);
            }

            // Print after a short delay to ensure all barcodes are rendered
            setTimeout(() => {
                printWindow.print();
            }, 1000);
        }, 500);

    } catch (error) {
        console.error("Error printing shipping labels:", error);
        
        toast?.update(toastId, {
            render: `Failed to print labels: ${error.message}`,
            type: "error",
            isLoading: false,
            autoClose: 3000,
        });
    }
}

// Helper function to generate the HTML for a single label
function generateLabelHTML(item, index, svgNodeId, barcodeId) {
	return `
    <!-- SCAC Code in top right -->
    <div class="scac-container">
      <div class="data-matrix-container">
        <span style="font-size: 21px; font-weight: 500; text-decoration: underline;">SCAC:</span>
        <span style="font-size: 25px; font-weight: 700;">${
			formData.shipment_type_info.tsp
		}</span>
      </div>
      <div id="${svgNodeId}" style="margin-top: 10px;"></div>
    </div>

    <!-- Main Content -->
    <div style="gap: 12px; width: 100%; display: flex; flex-direction: column;">
      <!-- Name Header -->
      <div class="header">
        <h1>${formData.shipper.lastName.toUpperCase()},</h1>
        <h1>${formData.shipper.firstName.toUpperCase()}</h1>
      </div>

      <!-- Registration Numbers -->
      <div class="reg-numbers">
        <div style="display: flex; flex-direction: column; line-height: 0.5;">
          <p style="margin: 0; font-size: 1.2em; text-decoration: underline; font-weight: bold;">REG#:</p>
          <h1 style="font-size: 60px; margin: 25px 0px;">${
				formData.shipment_type_info.registrationNumber
			}</h1>
        </div>
        <div></div>
      </div>

      <!-- Dates -->
      <div class="dates">
        <p style="margin: 0; font-weight: bold;">PICK UP DATE: ${formatDate(
			formData.shipper.pickupDate
		)}</p>
        <p style="margin: 0; font-weight: bold;">RDD: ${formatDate(
			formData.shipper.rdd
		)}</p>
      </div>

      <!-- Origin/Destination -->
      <div class="locations">
        <div>
          <p style="margin: 0; display: flex; gap: 10px; align-items: center;">
            <u>ORIGIN:</u>
            <span style="font-size: 25px; font-weight: bold;">${formatLocation(
				formData.origin
			)}</span>
          </p>
          
        </div>
        <div>
          <p style="margin: 0; display: flex; gap: 10px; align-items: center;">
            <u>DEST:</u>
            <span style="font-size: 25px; font-weight: bold;">${formatLocation(
				formData.destination
			)}</span>
          </p>
         
        </div>
      </div>

      <!-- Weight Information -->
      <div class="weights">
        <div class="weight-item">
          <span style="font-size: 25px; font-weight: 700; text-decoration: underline;">GROSS (Lbs):</span>
          <span style="font-size: 23px; font-weight: bold;">${
				item.grossWeight || "0"
			}</span>
        </div>
        <div class="weight-item">
          <span style="font-size: 25px; font-weight: 700; text-decoration: underline;">TARE (Lbs):</span>
          <span style="font-size: 23px; font-weight: bold;">${
				item.tareWeight || "0"
			}</span>
        </div>
        <div class="weight-item">
          <span style="font-size: 25px; font-weight: 700; text-decoration: underline;">NET (Lbs):</span>
          <span style="font-size: 23px; font-weight: bold;">${
				item.netWeight || "0"
			}</span>
        </div>
        <div class="weight-item">
          <span style="font-size: 25px; font-weight: 700; text-decoration: underline;">CUBE (Ft):</span>
          <span style="font-size: 23px; font-weight: bold;">${parseFloat(
				item.cubes || 0
			).toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			})}</span>
        </div>
        <div class="weight-item">
          <span style="font-size: 25px; font-weight: 700; text-decoration: underline;">TYPE:</span>
          <span style="font-size: 23px; font-weight: bold;">${
				formData.shipment_type_info.shipmentType.startsWith("CODE_")
					? formData.shipment_type_info.shipmentType.substring(
							formData.shipment_type_info.shipmentType.indexOf(
								"CODE_"
							) + "CODE_".length
					  )
					: formData.shipment_type_info.shipmentType
			}</span>
        </div>
      </div>

      <!-- Barcode -->
      <div class="barcode-section">
        <div style="display: flex; justify-content: center; align-items: center;">
          <svg id="${barcodeId}" style="width: 100%;"></svg>
        </div>
        <div style="display: flex; justify-content: center; align-items: center;">
          <span style="font-size: 75px; font-weight: 700; text-decoration: underline;">${
				item.itemName
			} OF ${formData.line_items.length}</span>
        </div>
      </div>
    </div>
  `;
}

// Helper function to generate barcodes for a specific label
function generateBarcodesForLabel(printWindow, item, index) {
	if (!printWindow.DATAMatrix || !printWindow.JsBarcode) {
		console.error("Required libraries not loaded in print window");
		return;
	}

	// Create data for the data matrix
	const shipmentData = `<Shipment>
    <FullName>${formData.shipper.lastName}, ${
		formData.shipper.firstName
	}</FullName>
    <FirstName>${formData.shipper.firstName}</FirstName>
    <LastName>${formData.shipper.lastName}</LastName>
    <SCAC>${formData.shipment_type_info.tsp}</SCAC>
    <Reference>${formData.shipment_type_info.gbl}</Reference>
    <Item>${item.itemName}</Item>
    <PackageType>${item.packageType || ""}</PackageType>
    <PickupDate>${formatDate(formData.shipper.pickupDate)}</PickupDate>
    <RDD>${formatDate(formData.shipper.rdd)}</RDD>
    <PickupAdress>${formatLocation(formData.origin)}</PickupAdress>
    <DeliveryAdress>${formatLocation(formData.destination)}</DeliveryAdress>
    <OriginGBLOC></OriginGBLOC>
    <DestinationGBLOC></DestinationGBLOC>
    <GrossWeight>${item.grossWeight || "0"}</GrossWeight>
    <TareWeight>${item.tareWeight || "0"}</TareWeight>
    <NetWt>${item.netWeight || "0"}</NetWt>
    <Cube>${item.cubes || "0"}</Cube>
    <ItemCount>${index + 1} OF ${formData.line_items.length}</ItemCount>
    <TrackingNumber>${item?.barcode || formData.shipment_type_info.gbl}</TrackingNumber>
  </Shipment>`;

	// Generate data matrix
	const svgNodeEl = printWindow.document.getElementById(`svg-node-${index}`);
	if (svgNodeEl) {
		try {
			const svgNode = printWindow.DATAMatrix({
				msg: shipmentData,
				dim: 128,
			});
			svgNodeEl.appendChild(svgNode);
		} catch (err) {
			console.error("Error generating data matrix for label", index, err);
		}
	}

	// Generate barcode
	const barcodeEl = printWindow.document.getElementById(`barcode-${index}`);
	if (barcodeEl) {
		try {
			const barcodeValue =
				item?.barcode || formData.shipment_type_info.gbl;
			printWindow.JsBarcode(`#barcode-${index}`, barcodeValue, {
				width: 3,
				height: 100,
			});
		} catch (err) {
			console.error("Error generating barcode for label", index, err);
		}
	}
}

async function fetchProNumberForRegistration() {
	
	if (!shipmentUuid.value) return;
	
	
	isLoadingProNumber.value = true;

	try {
		const response = await fetch(
			`${import.meta.env["PUBLIC_API_URL"]}/shipments/${shipmentUuid.value}`,
			{
				method: "GET",
				headers: {
					Authorization: `Bearer ${localStorage.getItem(
						"auth_token"
					)}`,
				},
			}
		);

		if(response.ok) {
			let data = await response.json();
			if ("shipment" in data) {
				data = data["shipment"];
			}

			if(data['proNumber']) {
				proNumber.value = data["proNumber"];
			}
		}

	} catch (err) {	

		console.error('Error fetching PRO number:', error);
		proNumber.value = 'N/A';

	} finally {
		isLoadingProNumber.value = !true;
	}	
}

function openRegistrationCompleteModal() {
	showRegistrationCompleteModal.value = true;
	fetchProNumberForRegistration();
}


// Function to close the modal
function closeRegistrationCompleteModal() {
	showRegistrationCompleteModal.value = false;
}

function createNewMilitaryShipment() {
  closeRegistrationCompleteModal();
  setTimeout( () => {
	window.location.href="/consumer-shipment"
  }, 100)
}

// Function to exit to home
function exitToHome() {
  closeRegistrationCompleteModal();
  setTimeout( () => {
	window.location.href="/"
  }, 100)
}



</script>

<script>
const INPUT_CLASS =
	"rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium placeholder:text-[85%] placeholder:text-gray-400 disabled:bg-gray-200 disabled:text-gray-300";

// const MIMES = "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/rtf, text/plain, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, text/csv, application/xml".split(", ");

const MIMES = "text/csv".split(", ");

const $ = (selector) => document.querySelector(selector);

export default {
	setup() {
		return {
			INPUT_CLASS,
			MIMES,
		};
	},
};

// this.$nextTick(() => {
// 			let j = setTimeout( () => {

// 				const datePickerIcons = [
// 					document.querySelector("#spdsvg"),
// 					document.querySelector("#oecpsvg"),
// 					document.querySelector("#decpsvg"),
// 					document.querySelector("#srdd")
// 				];

// 				datePickerIcons.forEach(datePickerIcon => {
// 					console.log(datePickerIcon);
// 					if (datePickerIcon) {
// 						datePickerIcon.addEventListener("click", () => {
// 							datePickerIcon.parentElement.lastElementChild.click();
// 						});
// 					}
// 				});

// 			}, 5000)
// 		});
</script>

<style lang="css" scoped>
label:not(.not-required):after {
	content: " *";
}

label {
	cursor: pointer !important;
}

input::-webkit-calendar-picker-indicator {
	opacity: 0 !important;
	position: absolute;
	margin-top: -6px;
	right: 12px;
}

/* Add smooth scrolling for the line items */
.overflow-x-auto {
	scroll-behavior: smooth;
	-webkit-overflow-scrolling: touch;
}

/* Custom scrollbar for the line items */
.overflow-x-auto::-webkit-scrollbar {
	height: 6px;
}

.overflow-x-auto::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 10px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
	background: #020754;
	border-radius: 10px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
	background: #a91d38;
}
</style>

<template>
	<!-- Loading Spinner while checking authentication -->
	<div
		v-if="isLoading"
		class="w-full h-screen flex items-center justify-center bg-page"
	>
		<div class="flex flex-col items-center">
			<div
				class="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"
			></div>
			<p class="mt-4 text-primary font-monster font-medium">Loading...</p>
		</div>
	</div>

	<!-- Main content - only shown when authenticated and data is loaded -->
	<div
		v-else
		class="w-[calc(100vw-4rem)] flex-grow flex flex-col pl-6 pr-8 py-7 gap-y-8 overflow-y-scroll"
	>
		<div class="p-2 bg-page">
			<div class="flex items-center">
				<h4 class="text-primary font-bold font-monster -ml-2">
					{{
						`${
							!isCompleted
								? isEditMode
									? "Edit Consumer Shipment"
									: "Consumer Shipment Registration"
								: "View Consumer Shipment"
						}`
					}}
				</h4>
			</div>
		</div>

		<div v-if="progress == 1" class="w-full flex flex-col gap-y-6">
			<div class="flex flex-row w-full gap-4 -mt-6">
				<div class="flex flex-col w-full"></div>
				<div class="flex flex-col w-full"></div>
				<div class="flex flex-col items-start gap-2 min-w-[250px]">
					<label
						class="font-normal font-monster text-secondary not-required"
						for="fileTag"
					>
						Shipment Data
					</label>
					<button
						@click="toggleActionModal"
						class="w-[250px] font-monster flex justify-center gap-4 items-center font-semibold bg-secondary hover:bg-secondary/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-white p-3"
					>
						<svg
							width="13"
							height="13"
							viewBox="0 0 13 13"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M4.81483 3.25932C3.28303 4.84418 3.6173 7.47431 5.37194 8.63942C5.42976 8.67783 5.50668 8.67021 5.55634 8.62169C5.92573 8.26075 6.23823 7.91114 6.51186 7.46651C6.55372 7.39848 6.52769 7.31021 6.45745 7.27216C6.18981 7.12716 5.9235 6.85528 5.77358 6.56809L5.7734 6.56819C5.59381 6.21058 5.53266 5.80971 5.62774 5.3957C5.62784 5.39573 5.62795 5.39576 5.62805 5.39576C5.73745 4.86572 6.30649 4.37266 6.74109 3.91667C6.74018 3.91636 6.73929 3.91603 6.73837 3.91573L8.36672 2.25377C9.01564 1.59147 10.083 1.586 10.7387 2.24166C11.401 2.89055 11.4119 3.96333 10.763 4.62562L9.77671 5.63987C9.73107 5.68681 9.71626 5.7553 9.73762 5.8172C9.96472 6.47575 10.0206 7.40432 9.86839 8.10588C9.86412 8.12551 9.88836 8.13837 9.9024 8.12401L12.0016 5.9815C13.3426 4.61284 13.3312 2.38447 11.9763 1.02957C10.5936 -0.353138 8.34259 -0.341631 6.97408 1.0551L4.82323 3.25032C4.82039 3.25332 4.8177 3.25637 4.81483 3.25932Z"
								fill="white"
							/>
							<path
								d="M8.73926 8.94129V8.94129L8.7431 8.93985C9.17151 8.15647 9.25584 7.25803 9.05506 6.38219L9.05415 6.38313L9.05316 6.3827C8.86252 5.60262 8.33941 4.82801 7.62897 4.35109C7.56785 4.31007 7.47023 4.31482 7.413 4.3611C7.05313 4.65208 6.70088 5.02521 6.46845 5.50137C6.43194 5.57613 6.45928 5.66593 6.53122 5.70769C6.80096 5.86429 7.04457 6.09357 7.20767 6.39796C7.33478 6.61299 7.4603 7.02082 7.37916 7.45921V7.45921C7.30346 8.04043 6.71635 8.57357 6.24976 9.05398C5.89459 9.41718 4.99195 10.3378 4.63043 10.7072C3.98153 11.3695 2.90875 11.3804 2.24647 10.7315C1.58419 10.0826 1.57324 9.00983 2.22214 8.34755L3.21137 7.33024C3.2562 7.28414 3.27145 7.21713 3.2515 7.15599C3.03185 6.48204 2.97168 5.5744 3.11032 4.87364C3.11418 4.8541 3.09015 4.84163 3.07621 4.85585L1.0086 6.96613C-0.346138 8.34884 -0.334657 10.6001 1.03416 11.9689C2.41679 13.3236 4.65642 13.3007 6.01111 11.918C6.48173 11.3916 8.49632 9.52635 8.73926 8.94129Z"
								fill="white"
							/>
						</svg>
						Upload From File
					</button>
				</div>
			</div>
			<div class="flex flex-row w-full gap-4">
				<div class="flex flex-col w-full">
					<label
						class="font-normal font-monster text-secondary"
						for="shipmentType"
					>
						Shipment Type
					</label>
					<div class="relative !w-full *:cursor-pointer">
						<select
							name="shipmentType"
							id="shipmentType"
							v-model="formData.shipment_type_info.shipmentType"
							:class="
								twMerge(
									INPUT_CLASS,
									'peer w-full shadow-[2px_2px_6px_rgba(187,195,206,0.4)] z-10',
									clsx({ error: false })
								)
							"
							style="
								appearance: none;
								text-indent: 1px;
								text-overflow: '';
							"
							:disabled="isLoadingServiceTypes"
						>
							<option value="" class="text-gray-400">
								{{
									isLoadingServiceTypes
										? "Loading..."
										: "Select a shipment type"
								}}
							</option>
							<option
								v-for="type in serviceTypes"
								:key="type.id"
								:value="
									type.name.toUpperCase().split(' ').join('_')
								"
							>
								{{ type.name }}
							</option>
						</select>
						<div
							v-if="isLoadingServiceTypes"
							class="absolute right-10 top-3.5"
						>
							<svg
								class="animate-spin h-5 w-5 text-primary"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
							>
								<circle
									class="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									stroke-width="4"
								></circle>
								<path
									class="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								></path>
							</svg>
						</div>
						<svg
							class="absolute z-[1] right-4 top-[40%] peer-open:rotate-180"
							width="23"
							height="13"
							viewBox="0 0 23 13"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								fill-rule="evenodd"
								clip-rule="evenodd"
								d="M2.71933 0.466564C2.09725 -0.155521 1.08865 -0.155521 0.466564 0.466564C-0.155521 1.08865 -0.155521 2.09725 0.466564 2.71933L10.0243 12.277C10.6463 12.8991 11.6549 12.8991 12.277 12.277L21.8347 2.71933C22.4568 2.09725 22.4568 1.08865 21.8347 0.466564C21.2126 -0.155521 20.204 -0.155521 19.5819 0.466564L11.1506 8.89787L2.71933 0.466564Z"
								fill="#020754"
							/>
						</svg>
					</div>
				</div>
				<div class="flex flex-col w-full">
					<label
						class="font-normal font-monster text-secondary"
						for="gbl"
					>
						GBL #
					</label>
					<input
						id="gbl"
						name="gbl"
						v-model="formData.shipment_type_info.gbl"
						placeholder="Government Bill of Lading"
						:class="
							twMerge(
								INPUT_CLASS,
								'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
								clsx({ error: false })
							)
						"
					/>
				</div>
				<div class="flex flex-col w-full">
					<label
						class="font-normal font-monster text-secondary not-required"
						for="registrationNumber"
					>
						Your Registration #
					</label>
					<input
						id="registrationNumber"
						name="registrationNumber"
						v-model="formData.shipment_type_info.registrationNumber"
						placeholder="Partner BOL"
						:class="
							twMerge(
								INPUT_CLASS,
								'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
								clsx({ error: false })
							)
						"
					/>
				</div>
			</div>
			<div class="flex flex-row w-full gap-4">
				<div class="flex flex-col w-full">
					<label
						class="font-normal font-monster text-secondary"
						for="tsp"
					>
						Carrier Name & SCAC
					</label>
					<input
						id="tsp"
						name="tsp"
						v-model="formData.shipment_type_info.tsp"
						:class="
							twMerge(
								INPUT_CLASS,
								'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
								clsx({ error: false })
							)
						"
					/>
				</div>
			<div class="flex flex-col w-full">
					<label
						class="font-normal font-monster text-secondary not-required"
						for="instructions"
					>
						Special Instructions
					</label>
					<textarea
						id="instructions"
						name="instructions"
						v-model="formData.shipment_type_info.instructions"
						:class="
							twMerge(
								INPUT_CLASS,
								'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
								clsx({ error: false })
							)
						"
					></textarea>
				</div>
				
			</div>
			<div class="flex flex-row w-full justify-end">
				<button
					@click="goToNextStep"
					class="w-[150px] font-monster flex justify-center gap-4 items-center font-semibold bg-primary hover:bg-primary/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-white p-3"
				>
					Next
				</button>
			</div>
		</div>
		<div v-if="progress == 2" class="w-full flex flex-col gap-y-6">
			<section class="w-full flex flex-col gap-y-4">
				<div class="flex flex-row w-full gap-4 -mt-6">
					<div class="flex flex-col w-full">
						<h6 class="text-secondary font-bold">
							Shipper Information
						</h6>
					</div>
				</div>
				<div class="flex flex-row w-full gap-4">
					<div class="flex flex-col w-full">
						<label
							class="font-normal font-monster text-secondary"
							for="sfn"
						>
							First Name
						</label>
						<input
							id="sfn"
							name="sfn"
							v-model="formData.shipper.firstName"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
									clsx({ error: false })
								)
							"
						/>
					</div>
					<div class="flex flex-col w-full">
						<label
							class="font-normal font-monster text-secondary"
							for="sln"
						>
							Last Name
						</label>
						<input
							id="sln"
							name="sln"
							v-model="formData.shipper.lastName"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
									clsx({ error: false })
								)
							"
						/>
					</div>
					<div class="flex flex-col w-full relative *:cursor-pointer">
						<label
							class="font-normal font-monster not-required text-secondary"
							for="spd"
						>
							Pickup Date
						</label>
						<svg
							id="spdsvg"
							class="absolute right-4 top-[50%]"
							width="22"
							height="22"
							viewBox="0 0 22 22"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g clip-path="url(#clip0_541_282)">
								<path
									d="M16.5 4.58337H5.50002C4.4875 4.58337 3.66669 5.40419 3.66669 6.41671V17.4167C3.66669 18.4292 4.4875 19.25 5.50002 19.25H16.5C17.5125 19.25 18.3334 18.4292 18.3334 17.4167V6.41671C18.3334 5.40419 17.5125 4.58337 16.5 4.58337Z"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M14.6667 2.75V6.41667"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M7.33331 2.75V6.41667"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M3.66669 10.0834H18.3334"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M10.0833 13.75H11"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M11 13.75V16.5"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</g>
							<defs>
								<clipPath id="clip0_541_282">
									<rect width="22" height="22" fill="white" />
								</clipPath>
							</defs>
						</svg>

						<input
							id="spd"
							name="spd"
							ref="pickupDatePickerEl"
							v-model="formData.shipper.pickupDate"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)] cursor-pointer',
									clsx({ error: false })
								)
							"
						/>
					</div>
				</div>
				<div class="flex flex-row w-full gap-4">
					<div class="flex flex-col w-full relative *:cursor-pointer">
						<label
							class="font-normal font-monster not-required text-secondary"
							for="srdd"
						>
							RDD
						</label>
						<svg
							id="srddsvg"
							class="absolute right-4 top-[50%]"
							width="22"
							height="22"
							viewBox="0 0 22 22"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g clip-path="url(#clip0_541_282)">
								<path
									d="M16.5 4.58337H5.50002C4.4875 4.58337 3.66669 5.40419 3.66669 6.41671V17.4167C3.66669 18.4292 4.4875 19.25 5.50002 19.25H16.5C17.5125 19.25 18.3334 18.4292 18.3334 17.4167V6.41671C18.3334 5.40419 17.5125 4.58337 16.5 4.58337Z"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M14.6667 2.75V6.41667"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M7.33331 2.75V6.41667"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M3.66669 10.0834H18.3334"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M10.0833 13.75H11"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M11 13.75V16.5"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</g>
							<defs>
								<clipPath id="clip0_541_282">
									<rect width="22" height="22" fill="white" />
								</clipPath>
							</defs>
						</svg>

						<input
							id="srdd"
							name="srdd"
							ref="rddDatePickerEl"
							v-model="formData.shipper.rdd"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)] cursor-pointer',
									clsx({ error: false })
								)
							"
						/>
					</div>
					<div
						class="flex flex-col w-full relative *:cursor-pointer"
					></div>
					<div
						class="flex flex-col w-full relative *:cursor-pointer"
					></div>
				</div>
			</section>
			<section class="w-full flex flex-col gap-y-4">
				<div class="flex flex-row w-full gap-4">
					<div class="flex flex-col w-full">
						<h6 class="text-secondary font-bold">
							Origin Information
						</h6>
					</div>
				</div>
				<div class="flex flex-row w-full gap-4">
					<div class="flex flex-col w-full">
						<label
							class="font-normal font-monster text-secondary"
							for="ooc"
						>
							Origin Country
						</label>
						<input
							list="ooc-country"
							id="ooc"
							name="ooc"
							ref="ooc"
							v-model="formData.origin.country"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
									clsx({ error: false })
								)
							"
						/>
						<datalist id="ooc-country">
							<option
								v-for="country in CountryList"
								:key="country.isoCode"
								:value="country.name"
							></option>
						</datalist>
					</div>
					<div class="flex flex-col w-full">
						<label
							:class="
								twMerge(
									'font-normal font-monster text-secondary',
									clsx({
										'not-required':
											formData.origin.country !==
												'United States' &&
											formData.origin.country !== 'US',
									})
								)
							"
							for="oos"
							>Origin State/District</label
						>
						<input
							id="oos"
							list="ooc-state-district"
							name="oos"
							v-model="formData.origin.state"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
									clsx({ error: false })
								)
							"
							ref="oos"
						/>
						<datalist id="ooc-state-district">
							<option
								v-for="state in originStates"
								:key="state.isoCode"
								:value="
									isUSOnlyOrigin ? state.isoCode : state.name
								"
							>
								{{ state.name }}
							</option>
						</datalist>
					</div>
					<div class="flex flex-col w-full relative">
						<label
							:class="
								twMerge(
									'font-normal font-monster text-secondary',
									clsx({
										'not-required':
											formData.origin.country !==
												'United States' &&
											formData.origin.country !== 'US',
									})
								)
							"
							for="ooy"
							>Origin City</label
						>
						<input
							list="ooc-city"
							id="ooy"
							name="ooy"
							v-model="formData.origin.city"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
									clsx({ error: false })
								)
							"
							for="ooy"
						/>
						<datalist id="ooc-city">
							<option
								v-for="city in originCity"
								:key="city.isoCode"
								:value="city.name"
							>
								{{ city.name }}
							</option>
						</datalist>
					</div>
				</div>
				<div class="flex flex-row w-full gap-4">
					<div class="flex flex-col w-full relative">
						<label
							:class="
								twMerge(
									'font-normal font-monster text-secondary',
									clsx({
										'not-required':
											formData.origin.country !==
												'United States' &&
											formData.origin.country !== 'US',
									})
								)
							"
							for="ooz"
						>
							Origin Zip/Postal Code
						</label>
						<input
							id="ooz"
							name="ooz"
							v-model="formData.origin.zip"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
									clsx({ error: false })
								)
							"
						/>
					</div>
					<div class="flex flex-col w-full relative">
						<label
							class="font-normal font-monster text-secondary"
							for="opa"
						>
							Origin Port & Agent
						</label>
						<div class="relative">
							<input
								id="opa"
								name="opa"
								list="origin-port-agents"
								v-model="formData.origin.portAgent"
								:class="
									twMerge(
										INPUT_CLASS,
										'shadow-[2px_2px_6px_rgba(187,195,206,0.4)] w-full',
										clsx({ error: false })
									)
								"
								ref="opa"
								placeholder="Select port agent"
							/>
							<span
								v-if="isLoadingOriginPortAgents"
								class="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary"
							>
								<svg
									class="animate-spin h-5 w-5"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										class="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										stroke-width="4"
									></circle>
									<path
										class="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									></path>
								</svg>
							</span>
							<datalist id="origin-port-agents">
								<option
									v-for="agent in originPortAgents"
									:key="agent.id"
									:value="
										agent.port_name +
										' / ' +
										agent.agent_name
									"
								>
									{{ agent.port_abbr }}
								</option>
							</datalist>
						</div>
					</div>
					<div class="flex flex-col w-full relative *:cursor-pointer">
						<label
							class="font-normal font-monster not-required text-secondary"
							for="oecp"
						>
							ETA to Coleman Port
						</label>
						<svg
							id="oecpsvg"
							class="absolute right-4 top-[50%]"
							width="22"
							height="22"
							viewBox="0 0 22 22"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							onclick=""
						>
							<g clip-path="url(#clip0_541_282)">
								<path
									d="M16.5 4.58337H5.50002C4.4875 4.58337 3.66669 5.40419 3.66669 6.41671V17.4167C3.66669 18.4292 4.4875 19.25 5.50002 19.25H16.5C17.5125 19.25 18.3334 18.4292 18.3334 17.4167V6.41671C18.3334 5.40419 17.5125 4.58337 16.5 4.58337Z"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M14.6667 2.75V6.41667"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M7.33331 2.75V6.41667"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M3.66669 10.0834H18.3334"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M10.0833 13.75H11"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M11 13.75V16.5"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</g>
							<defs>
								<clipPath id="clip0_541_282">
									<rect width="22" height="22" fill="white" />
								</clipPath>
							</defs>
						</svg>

						<input
							id="oecp"
							name="oecp"
							ref="etaOriginDatePickerEl"
							v-model="formData.origin.etaToPort"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)] cursor-pointer',
									clsx({ error: false })
								)
							"
						/>
					</div>
				</div>
			</section>
			<section class="w-full flex flex-col gap-y-4">
				<div class="flex flex-row w-full gap-4">
					<div class="flex flex-col w-full">
						<h6 class="text-secondary font-bold">
							Destination Information
						</h6>
					</div>
				</div>
				<div class="flex flex-row w-full gap-4">
					<div class="flex flex-col w-full">
						<label
							class="font-normal font-monster text-secondary"
							for="ddc"
						>
							Destination Country
						</label>
						<input
							list="ddc-country"
							id="ddc"
							name="ddc"
							v-model="formData.destination.country"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
									clsx({ error: false })
								)
							"
							ref="ddc"
						/>

						<datalist id="ddc-country">
							<option
								v-for="country in CountryList"
								:key="country.isoCode"
								:value="country.name"
							>
								{{ country.name }}
							</option>
						</datalist>
					</div>
					<div class="flex flex-col w-full">
						<label
							:class="
								twMerge(
									'font-normal font-monster text-secondary',
									clsx({
										'not-required':
											formData.destination.country !==
												'United States' &&
											formData.destination.country !==
												'US',
									})
								)
							"
							for="dds"
							>Destination State/District</label
						>
						<input
							id="dds"
							list="dds-state-district"
							name="dds"
							v-model="formData.destination.state"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
									clsx({ error: false })
								)
							"
							ref="dds"
						/>
						<datalist id="dds-state-district">
							<option
								v-for="state in destinationStates"
								:key="state.isoCode"
								:value="
									isUSOnlyDestination
										? state.isoCode
										: state.name
								"
							>
								{{ state.name }}
							</option>
						</datalist>
					</div>
					<div class="flex flex-col w-full relative">
						<label
							:class="
								twMerge(
									'font-normal font-monster text-secondary',
									clsx({
										'not-required':
											formData.destination.country !==
												'United States' &&
											formData.destination.country !==
												'US',
									})
								)
							"
							for="ddy"
							>Destination City</label
						>
						<input
							list="ddc-city"
							id="ddy"
							name="ddy"
							v-model="formData.destination.city"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
									clsx({ error: false })
								)
							"
							ref="ddy"
						/>
						<datalist id="ddc-city">
							<option
								v-for="city in destinationCity"
								:key="city.isoCode"
								:value="city.name"
							>
								{{ city.name }}
							</option>
						</datalist>
					</div>
				</div>
				<div class="flex flex-row w-full gap-4">
					<div class="flex flex-col w-full relative">
						<label
							:class="
								twMerge(
									'font-normal font-monster text-secondary',
									clsx({
										'not-required':
											formData.destination.country !==
												'United States' &&
											formData.destination.country !==
												'US',
									})
								)
							"
							for="ddz"
						>
							Destination Zip/Postal Code
						</label>
						<input
							id="ddz"
							name="ddz"
							v-model="formData.destination.zip"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]',
									clsx({ error: false })
								)
							"
						/>
					</div>
					<div class="flex flex-col w-full relative">
						<label
							class="font-normal font-monster text-secondary"
							for="dpa"
						>
							Destination Port & Agent
						</label>
						<div class="relative">
							<input
								id="dpa"
								name="dpa"
								list="destination-port-agents"
								v-model="formData.destination.portAgent"
								:class="
									twMerge(
										INPUT_CLASS,
										'shadow-[2px_2px_6px_rgba(187,195,206,0.4)] w-full',
										clsx({ error: false })
									)
								"
								placeholder="Select port agent"
								ref="dpa"
							/>
							<span
								v-if="isLoadingDestinationPortAgents"
								class="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary"
							>
								<svg
									class="animate-spin h-5 w-5"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										class="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										stroke-width="4"
									></circle>
									<path
										class="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									></path>
								</svg>
							</span>
							<datalist id="destination-port-agents">
								<option
									v-for="agent in destinationPortAgents"
									:key="agent.id"
									:value="
										agent.port_name +
										' / ' +
										agent.agent_name
									"
								>
									{{ agent.port_abbr }}
								</option>
							</datalist>
						</div>
					</div>
					<div class="flex flex-col w-full relative *:cursor-pointer">
						<label
							class="font-normal font-monster not-required text-secondary"
							for="decp"
						>
							ETA to Coleman Port
						</label>
						<svg
							id="decpsvg"
							class="absolute right-4 top-[50%]"
							width="22"
							height="22"
							viewBox="0 0 22 22"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g clip-path="url(#clip0_541_282)">
								<path
									d="M16.5 4.58337H5.50002C4.4875 4.58337 3.66669 5.40419 3.66669 6.41671V17.4167C3.66669 18.4292 4.4875 19.25 5.50002 19.25H16.5C17.5125 19.25 18.3334 18.4292 18.3334 17.4167V6.41671C18.3334 5.40419 17.5125 4.58337 16.5 4.58337Z"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M14.6667 2.75V6.41667"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M7.33331 2.75V6.41667"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M3.66669 10.0834H18.3334"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M10.0833 13.75H11"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M11 13.75V16.5"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</g>
							<defs>
								<clipPath id="clip0_541_282">
									<rect width="22" height="22" fill="white" />
								</clipPath>
							</defs>
						</svg>

						<input
							id="decp"
							name="decp"
							ref="etaDestDatePickerEl"
							v-model="formData.destination.etaToPort"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)] cursor-pointer',
									clsx({ error: false })
								)
							"
						/>
					</div>
				</div>
			</section>
			<div class="flex flex-row w-full justify-end gap-10">
				<button
					@click="goToPreviousStep"
					class="w-[150px] font-monster flex justify-center gap-4 items-center font-semibold bg-secondary hover:bg-secondary/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-white p-3"
				>
					Back
				</button>
				<button
					@click="goToNextStep"
					class="w-[150px] font-monster flex justify-center gap-4 items-center font-semibold bg-primary hover:bg-primary/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-white p-3"
				>
					Next
				</button>
			</div>
		</div>
		<div
			v-if="parseInt(progress) >= 3 && progress < 4"
			class="w-full flex flex-col gap-y-6"
		>
			<section class="w-full flex flex-col gap-y-4">
				<div class="flex flex-row w-full gap-4 -mt-6">
					<div class="flex flex-col w-full">
						<h6 class="text-secondary font-bold">Line Items</h6>
					</div>
				</div>
				<div class="flex flex-row w-full gap-4">
					<div class="flex flex-col w-full">
						<label
							class="font-normal font-monster text-secondary"
							for="lineItemQty"
						>
							Quantity
						</label>
						<input
							:disabled="isCompleted"
							id="lineItemQty"
							name="lineItemQty"
							type="number"
							min="1"
							max="999"
							v-model="lineItemsQuantity"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)] disabled:bg-white disabled:text-black',
									clsx({ error: false })
								)
							"
						/>
					</div>
					<div class="flex flex-col w-full"></div>
					<div class="flex flex-col w-full"></div>
				</div>

				<!-- Line Items Scroll Section - appears after quantity is entered -->
				<div v-if="lineItemsQuantity > 0" class="w-full mt-4">
					<div class="flex flex-row items-center mb-2">
						<h6 class="text-primary font-semibold">
							Line Items Details
						</h6>
						<span class="ml-2 text-sm text-gray-500"
							>(Scroll horizontally to view all items)</span
						>
					</div>

					<!-- Horizontal scrolling container -->
					<div class="relative overflow-x-auto pb-4">
						<div
							class="flex flex-row gap-4"
							style="min-width: max-content"
						>
							<!-- Line item card for each item in the array -->
							<div
								v-for="(item, index) in lineItems"
								:key="item.id"
								class="flex-shrink-0 w-[600px] bg-white rounded-xl p-4 shadow-[2px_2px_6px_rgba(187,195,206,0.4)] relative"
							>
								<!-- Add delete button to top right corner -->
								<button
									v-if="lineItemsQuantity > 1 && !isCompleted"
									@click="deleteLineItem(index)"
									class="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-primary bg-opacity-10 hover:bg-opacity-20 transition-all"
									title="Delete this item"
								>
									<svg
										width="16"
										height="16"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M18 6L6 18"
											stroke="#a91d38"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M6 6L18 18"
											stroke="#a91d38"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</button>

								<div
									class="flex justify-between items-center mb-3"
								>
									<h6
										class="text-primary ring-0 outline-none font-semibold focus-within:underline-offset-4 underline underline-offset-2"
										contenteditable="plaintext-only"
									>
										Item #{{ index + 1 }}
									</h6>
								</div>

								<div class="flex flex-col gap-3">
									<div class="flex flex-col">
										<label
											class="font-normal font-monster text-secondary text-sm"
											for="item-name-${index}"
										>
											Piece Number
										</label>
										<input
											:id="'item-name-' + index"
											v-model="item.itemName"
											:class="
												twMerge(
													INPUT_CLASS,
													'text-sm py-2',
													clsx({ error: false })
												)
											"
										/>
									</div>

									<div class="grid grid-cols-1 gap-2">
										<div class="flex flex-col">
											<label
												class="font-normal font-monster text-secondary text-sm"
												for="item-pkg-${index}"
											>
												Container Type
											</label>
											<div class="relative">
												<select
													:id="'item-pkg-' + index"
													v-model="item.packageType"
													:class="
														twMerge(
															INPUT_CLASS,
															'text-sm py-2 w-full',
															clsx({
																error: false,
															})
														)
													"
													:disabled="
														isLoadingContainerTypes
													"
												>
													<option value="">
														{{
															isLoadingContainerTypes
																? "Loading..."
																: "Select"
														}}
													</option>
													<option
														v-for="type in containerTypes"
														:key="type.id"
														:value="type.name"
													>
														{{ type.name }}
													</option>
												</select>
												<div
													v-if="
														isLoadingContainerTypes
													"
													class="absolute right-3 top-1/2 transform -translate-y-1/2"
												>
													<svg
														class="animate-spin h-4 w-4 text-primary"
														xmlns="http://www.w3.org/2000/svg"
														fill="none"
														viewBox="0 0 24 24"
													>
														<circle
															class="opacity-25"
															cx="12"
															cy="12"
															r="10"
															stroke="currentColor"
															stroke-width="4"
														></circle>
														<path
															class="opacity-75"
															fill="currentColor"
															d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
														></path>
													</svg>
												</div>
											</div>
										</div>
									</div>
									<hr />
									<div class="grid grid-cols-2 gap-2">
										<div class="flex flex-col col-span-2">
											<div
												class="flex items-center space-x-6 mt-1 py-1"
											>
												<label
													class="inline-flex items-center cursor-pointer not-required relative"
												>
													<input
														type="radio"
														:name="`dimension-unit-${index}`"
														:id="`dimension-unit-in-${index}`"
														value="in"
														v-model="
															item.dimensionUnit
														"
														class="hidden peer"
														checked
													/>
													<span
														class="w-4 h-4 border border-secondary rounded-full flex items-center justify-center mr-1.5 peer-checked:bg-white peer-checked:border-secondary"
													>
													</span>
													<span
														class="absolute w-2 h-2 left-[4px] top-[6px] rounded-full bg-secondary opacity-0 peer-checked:opacity-100"
													></span>
													<span
														class="text-sm text-secondary"
														>Inches</span
													>
												</label>
												<label
													class="inline-flex items-center cursor-pointer not-required relative"
												>
													<input
														type="radio"
														:name="`dimension-unit-${index}`"
														:id="`dimension-unit-cm-${index}`"
														value="cm"
														v-model="
															item.dimensionUnit
														"
														class="hidden peer"
													/>
													<span
														class="w-4 h-4 border border-secondary rounded-full flex items-center justify-center mr-1.5 peer-checked:bg-white peer-checked:border-secondary"
													>
													</span>
													<span
														class="absolute w-2 h-2 left-[4px] top-[6px] rounded-full bg-secondary opacity-0 peer-checked:opacity-100"
													></span>
													<span
														class="text-sm text-secondary"
														>CM</span
													>
												</label>
											</div>
										</div>

										<div class="flex flex-col">
											<label
												class="font-normal font-monster text-secondary text-sm not-required"
												for="item-length-${index}"
											>
												Length
											</label>
											<input
												:id="'item-length-' + index"
												min="0"
												type="number"
												v-model="item.length"
												:class="
													twMerge(
														INPUT_CLASS,
														'text-sm py-2',
														clsx({ error: false })
													)
												"
											/>
										</div>

										<div class="flex flex-col">
											<label
												class="font-normal font-monster text-secondary text-sm not-required"
												for="item-width-${index}"
											>
												Width
											</label>
											<input
												:id="'item-width-' + index"
												min="0"
												type="number"
												v-model="item.width"
												:class="
													twMerge(
														INPUT_CLASS,
														'text-sm py-2',
														clsx({ error: false })
													)
												"
											/>
										</div>

										<div class="flex flex-col">
											<label
												class="font-normal font-monster text-secondary text-sm not-required"
												for="item-height-${index}"
											>
												Height
											</label>
											<input
												:id="'item-height-' + index"
												min="0"
												type="number"
												v-model="item.height"
												:class="
													twMerge(
														INPUT_CLASS,
														'text-sm py-2',
														clsx({ error: false })
													)
												"
											/>
										</div>
										<div class="flex flex-col">
											<label
												class="font-normal font-monster text-secondary text-sm not-required"
												for="item-cubes-${index}"
											>
												Cubes
											</label>
											<input
												:id="'item-cubes-' + index"
												disabled
												min="0"
												type="number"
												v-model="item.cubes"
												:class="
													twMerge(
														INPUT_CLASS,
														'text-sm py-2 disabled:bg-white disabled:text-black',
														clsx({ error: false })
													)
												"
											/>
										</div>
									</div>
									<hr />
									<div class="grid grid-cols-3 gap-2">
										<div class="flex flex-col col-span-3">
											<div
												class="flex items-center space-x-6 mt-1 py-1"
											>
												<label
													class="inline-flex items-center cursor-pointer not-required relative"
												>
													<input
														type="radio"
														:name="`weight-unit-${index}`"
														:id="`weight-unit-lbs-${index}`"
														value="lbs"
														v-model="
															item.weightUnit
														"
														class="hidden peer"
														checked
													/>
													<span
														class="w-4 h-4 border border-secondary rounded-full flex items-center justify-center mr-1.5 peer-checked:bg-white peer-checked:border-secondary"
													>
													</span>
													<span
														class="absolute w-2 h-2 left-[4px] top-[6px] rounded-full bg-secondary opacity-0 peer-checked:opacity-100"
													></span>
													<span
														class="text-sm text-secondary"
														>Lbs</span
													>
												</label>
												<label
													class="inline-flex items-center cursor-pointer not-required relative"
												>
													<input
														type="radio"
														:name="`weight-unit-${index}`"
														:id="`weight-unit-kg-${index}`"
														value="kg"
														v-model="
															item.weightUnit
														"
														class="hidden peer"
													/>
													<span
														class="w-4 h-4 border border-secondary rounded-full flex items-center justify-center mr-1.5 peer-checked:bg-white peer-checked:border-secondary"
													>
													</span>
													<span
														class="absolute w-2 h-2 left-[4px] top-[6px] rounded-full bg-secondary opacity-0 peer-checked:opacity-100"
													></span>
													<span
														class="text-sm text-secondary"
														>Kg</span
													>
												</label>
											</div>
										</div>
										<div class="flex flex-col">
											<label
												class="font-normal font-monster text-secondary text-sm not-required"
												for="item-grossWeight-${index}"
											>
												Gross Weight
											</label>
											<input
												:id="
													'item-grossWeight-' + index
												"
												min="0"
												type="number"
												v-model="item.grossWeight"
												:class="
													twMerge(
														INPUT_CLASS,
														'text-sm py-2',
														clsx({ error: false })
													)
												"
											/>
										</div>

										<div class="flex flex-col">
											<label
												class="font-normal font-monster text-secondary text-sm not-required"
												for="item-tareWeight-${index}"
											>
												Tare Weight
											</label>
											<input
												:id="'item-tareWeight-' + index"
												min="0"
												type="number"
												v-model="item.tareWeight"
												:class="
													twMerge(
														INPUT_CLASS,
														'text-sm py-2',
														clsx({ error: false })
													)
												"
											/>
										</div>

										<div class="flex flex-col">
											<label
												class="font-normal font-monster text-secondary text-sm not-required"
												for="item-netWeight-${index}"
											>
												Net Weight
											</label>
											<input
												:id="'item-netWeight-' + index"
												disabled
												min="0"
												type="number"
												v-model="item.netWeight"
												:class="
													twMerge(
														INPUT_CLASS,
														'text-sm py-2 disabled:bg-white disabled:text-black',
														clsx({ error: false })
													)
												"
											/>
										</div>
										<div class="flex flex-col col-span-3">
											<div
												class="flex items-center space-x-6 mt-1 py-1"
											>
												<label
													class="inline-flex items-center cursor-pointer not-required relative"
													:for="`contains-firearms-${index}`"
												>
													<input
														type="checkbox"
														:name="`contains-firearms-${index}`"
														:id="`contains-firearms-${index}`"
														value="firearms"
														v-model="item.firearm"
														class="hidden peer"
													/>
													<svg
														class="block peer-checked:hidden"
														width="41"
														height="41"
														viewBox="0 0 41 41"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<g
															filter="url(#filter0_d_514_435)"
														>
															<rect
																x="4"
																y="4"
																width="29"
																height="29"
																rx="10"
																fill="white"
															/>
															<rect
																x="4.5"
																y="4.5"
																width="28"
																height="28"
																rx="9.5"
																stroke="#020754"
																stroke-opacity="0.8"
															/>
														</g>
														<defs>
															<filter
																id="filter0_d_514_435"
																x="0"
																y="0"
																width="41"
																height="41"
																filterUnits="userSpaceOnUse"
																color-interpolation-filters="sRGB"
															>
																<feFlood
																	flood-opacity="0"
																	result="BackgroundImageFix"
																/>
																<feColorMatrix
																	in="SourceAlpha"
																	type="matrix"
																	values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
																	result="hardAlpha"
																/>
																<feOffset
																	dx="2"
																	dy="2"
																/>
																<feGaussianBlur
																	stdDeviation="3"
																/>
																<feComposite
																	in2="hardAlpha"
																	operator="out"
																/>
																<feColorMatrix
																	type="matrix"
																	values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.4 0"
																/>
																<feBlend
																	mode="normal"
																	in2="BackgroundImageFix"
																	result="effect1_dropShadow_514_435"
																/>
																<feBlend
																	mode="normal"
																	in="SourceGraphic"
																	in2="effect1_dropShadow_514_435"
																	result="shape"
																/>
															</filter>
														</defs>
													</svg>

													<svg
														class="hidden peer-checked:block"
														width="41"
														height="41"
														viewBox="0 0 41 41"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<g
															filter="url(#filter0_d_773_70)"
														>
															<rect
																x="4"
																y="4"
																width="29"
																height="29"
																rx="10"
																fill="white"
															/>
															<rect
																x="4.5"
																y="4.5"
																width="28"
																height="28"
																rx="9.5"
																stroke="#020754"
																stroke-opacity="0.8"
															/>
														</g>
														<path
															d="M14 19.7572L17.5065 23L24 17"
															stroke="#020754"
															stroke-opacity="0.8"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
														/>
														<defs>
															<filter
																id="filter0_d_773_70"
																x="0"
																y="0"
																width="41"
																height="41"
																filterUnits="userSpaceOnUse"
																color-interpolation-filters="sRGB"
															>
																<feFlood
																	flood-opacity="0"
																	result="BackgroundImageFix"
																/>
																<feColorMatrix
																	in="SourceAlpha"
																	type="matrix"
																	values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
																	result="hardAlpha"
																/>
																<feOffset
																	dx="2"
																	dy="2"
																/>
																<feGaussianBlur
																	stdDeviation="3"
																/>
																<feComposite
																	in2="hardAlpha"
																	operator="out"
																/>
																<feColorMatrix
																	type="matrix"
																	values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.4 0"
																/>
																<feBlend
																	mode="normal"
																	in2="BackgroundImageFix"
																	result="effect1_dropShadow_773_70"
																/>
																<feBlend
																	mode="normal"
																	in="SourceGraphic"
																	in2="effect1_dropShadow_773_70"
																	result="shape"
																/>
															</filter>
														</defs>
													</svg>

													<span
														class="text-sm text-secondary"
														>Contains Firearms</span
													>
												</label>
												<label
													class="inline-flex items-center cursor-pointer not-required relative"
													:for="`contains-hazmat-${index}`"
												>
													<input
														type="checkbox"
														:name="`contains-hazmat-${index}`"
														:id="`contains-hazmat-${index}`"
														value="hazmat"
														v-model="item.hazmat"
														class="hidden peer"
													/>
													<svg
														class="block peer-checked:hidden"
														width="41"
														height="41"
														viewBox="0 0 41 41"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<g
															filter="url(#filter0_d_514_435)"
														>
															<rect
																x="4"
																y="4"
																width="29"
																height="29"
																rx="10"
																fill="white"
															/>
															<rect
																x="4.5"
																y="4.5"
																width="28"
																height="28"
																rx="9.5"
																stroke="#020754"
																stroke-opacity="0.8"
															/>
														</g>
														<defs>
															<filter
																id="filter0_d_514_435"
																x="0"
																y="0"
																width="41"
																height="41"
																filterUnits="userSpaceOnUse"
																color-interpolation-filters="sRGB"
															>
																<feFlood
																	flood-opacity="0"
																	result="BackgroundImageFix"
																/>
																<feColorMatrix
																	in="SourceAlpha"
																	type="matrix"
																	values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
																	result="hardAlpha"
																/>
																<feOffset
																	dx="2"
																	dy="2"
																/>
																<feGaussianBlur
																	stdDeviation="3"
																/>
																<feComposite
																	in2="hardAlpha"
																	operator="out"
																/>
																<feColorMatrix
																	type="matrix"
																	values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.4 0"
																/>
																<feBlend
																	mode="normal"
																	in2="BackgroundImageFix"
																	result="effect1_dropShadow_514_435"
																/>
																<feBlend
																	mode="normal"
																	in="SourceGraphic"
																	in2="effect1_dropShadow_514_435"
																	result="shape"
																/>
															</filter>
														</defs>
													</svg>

													<svg
														class="hidden peer-checked:block"
														width="41"
														height="41"
														viewBox="0 0 41 41"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<g
															filter="url(#filter0_d_773_70)"
														>
															<rect
																x="4"
																y="4"
																width="29"
																height="29"
																rx="10"
																fill="white"
															/>
															<rect
																x="4.5"
																y="4.5"
																width="28"
																height="28"
																rx="9.5"
																stroke="#020754"
																stroke-opacity="0.8"
															/>
														</g>
														<path
															d="M14 19.7572L17.5065 23L24 17"
															stroke="#020754"
															stroke-opacity="0.8"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
														/>
														<defs>
															<filter
																id="filter0_d_773_70"
																x="0"
																y="0"
																width="41"
																height="41"
																filterUnits="userSpaceOnUse"
																color-interpolation-filters="sRGB"
															>
																<feFlood
																	flood-opacity="0"
																	result="BackgroundImageFix"
																/>
																<feColorMatrix
																	in="SourceAlpha"
																	type="matrix"
																	values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
																	result="hardAlpha"
																/>
																<feOffset
																	dx="2"
																	dy="2"
																/>
																<feGaussianBlur
																	stdDeviation="3"
																/>
																<feComposite
																	in2="hardAlpha"
																	operator="out"
																/>
																<feColorMatrix
																	type="matrix"
																	values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.4 0"
																/>
																<feBlend
																	mode="normal"
																	in2="BackgroundImageFix"
																	result="effect1_dropShadow_773_70"
																/>
																<feBlend
																	mode="normal"
																	in="SourceGraphic"
																	in2="effect1_dropShadow_773_70"
																	result="shape"
																/>
															</filter>
														</defs>
													</svg>

													<span
														class="text-sm text-secondary"
														>Contains Hazmat</span
													>
												</label>
												<label
													class="inline-flex items-center cursor-pointer not-required relative"
													:for="`contains-lion-${index}`"
												>
													<input
														type="checkbox"
														:name="`contains-lion-${index}`"
														:id="`contains-lion-${index}`"
														value="lion"
														v-model="item.lion"
														class="hidden peer"
													/>
													<svg
														class="block peer-checked:hidden"
														width="41"
														height="41"
														viewBox="0 0 41 41"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<g
															filter="url(#filter0_d_514_435)"
														>
															<rect
																x="4"
																y="4"
																width="29"
																height="29"
																rx="10"
																fill="white"
															/>
															<rect
																x="4.5"
																y="4.5"
																width="28"
																height="28"
																rx="9.5"
																stroke="#020754"
																stroke-opacity="0.8"
															/>
														</g>
														<defs>
															<filter
																id="filter0_d_514_435"
																x="0"
																y="0"
																width="41"
																height="41"
																filterUnits="userSpaceOnUse"
																color-interpolation-filters="sRGB"
															>
																<feFlood
																	flood-opacity="0"
																	result="BackgroundImageFix"
																/>
																<feColorMatrix
																	in="SourceAlpha"
																	type="matrix"
																	values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
																	result="hardAlpha"
																/>
																<feOffset
																	dx="2"
																	dy="2"
																/>
																<feGaussianBlur
																	stdDeviation="3"
																/>
																<feComposite
																	in2="hardAlpha"
																	operator="out"
																/>
																<feColorMatrix
																	type="matrix"
																	values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.4 0"
																/>
																<feBlend
																	mode="normal"
																	in2="BackgroundImageFix"
																	result="effect1_dropShadow_514_435"
																/>
																<feBlend
																	mode="normal"
																	in="SourceGraphic"
																	in2="effect1_dropShadow_514_435"
																	result="shape"
																/>
															</filter>
														</defs>
													</svg>

													<svg
														class="hidden peer-checked:block"
														width="41"
														height="41"
														viewBox="0 0 41 41"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<g
															filter="url(#filter0_d_773_70)"
														>
															<rect
																x="4"
																y="4"
																width="29"
																height="29"
																rx="10"
																fill="white"
															/>
															<rect
																x="4.5"
																y="4.5"
																width="28"
																height="28"
																rx="9.5"
																stroke="#020754"
																stroke-opacity="0.8"
															/>
														</g>
														<path
															d="M14 19.7572L17.5065 23L24 17"
															stroke="#020754"
															stroke-opacity="0.8"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
														/>
														<defs>
															<filter
																id="filter0_d_773_70"
																x="0"
																y="0"
																width="41"
																height="41"
																filterUnits="userSpaceOnUse"
																color-interpolation-filters="sRGB"
															>
																<feFlood
																	flood-opacity="0"
																	result="BackgroundImageFix"
																/>
																<feColorMatrix
																	in="SourceAlpha"
																	type="matrix"
																	values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
																	result="hardAlpha"
																/>
																<feOffset
																	dx="2"
																	dy="2"
																/>
																<feGaussianBlur
																	stdDeviation="3"
																/>
																<feComposite
																	in2="hardAlpha"
																	operator="out"
																/>
																<feColorMatrix
																	type="matrix"
																	values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.4 0"
																/>
																<feBlend
																	mode="normal"
																	in2="BackgroundImageFix"
																	result="effect1_dropShadow_773_70"
																/>
																<feBlend
																	mode="normal"
																	in="SourceGraphic"
																	in2="effect1_dropShadow_773_70"
																	result="shape"
																/>
															</filter>
														</defs>
													</svg>

													<span
														class="text-sm text-secondary"
														>Contains Lion
														Batteries</span
													>
												</label>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Scroll indicators -->
					<div class="flex justify-center mt-2">
						<div class="flex items-center gap-1">
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9.25 4.75L3.75 12L9.25 19.25"
									stroke="#020754"
									stroke-width="1.5"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M14.75 4.75L20.25 12L14.75 19.25"
									stroke="#020754"
									stroke-width="1.5"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
							<span class="text-xs text-secondary"
								>Scroll to see all items</span
							>
						</div>
					</div>
				</div>
			</section>
			<div class="flex flex-row w-full justify-end gap-10">
				<button
					@click="goToPreviousStep"
					class="w-[150px] font-monster flex justify-center gap-4 items-center font-semibold bg-secondary hover:bg-secondary/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-white p-3"
				>
					Back
				</button>
				<button
					@click="goToNextStep"
					class="w-[150px] font-monster flex justify-center gap-4 items-center font-semibold bg-primary hover:bg-primary/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-white p-3"
				>
					Next
				</button>
			</div>
		</div>
		<div v-if="progress == 4" class="w-full flex flex-col gap-y-6">
			<section class="w-full flex flex-col gap-y-4">
				<div class="flex flex-row w-full gap-4 -mt-6">
					<div class="flex flex-col w-full">
						<h6 class="text-secondary font-bold">
							Ocean Shipment Information
						</h6>
					</div>
				</div>
				<div class="flex flex-row w-full gap-4">
					<div class="flex flex-col w-full relative *:cursor-pointer">
						<label
							class="font-normal font-monster not-required text-secondary"
							for="obol"
						>
							OBOL #
						</label>
						<input
							id="obol"
							name="obol"
							v-model="formData.oceanShipment.obolNumber"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)] cursor-pointer',
									clsx({ error: false })
								)
							"
						/>
					</div>
					<div class="flex flex-col w-full relative *:cursor-pointer">
						<label
							class="font-normal font-monster not-required text-secondary"
							for="ocnt"
						>
							Container #
						</label>
						<input
							id="ocnt"
							name="ocnt"
							v-model="formData.oceanShipment.containerNumber"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)] cursor-pointer',
									clsx({ error: false })
								)
							"
						/>
					</div>
					<div class="flex flex-col w-full relative *:cursor-pointer">
						<label
							class="font-normal font-monster not-required text-secondary"
							for="stml"
						>
							Steamship Line
						</label>
						<input
							id="stml"
							name="stml"
							v-model="formData.oceanShipment.steamshipLine"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)] cursor-pointer',
									clsx({ error: false })
								)
							"
						/>
					</div>
				</div>
				<div class="flex flex-row w-full gap-4">
					<div class="flex flex-col w-full relative *:cursor-pointer">
						<label
							class="font-normal font-monster not-required text-secondary"
							for="ocnt"
						>
							Vessel Name
						</label>
						<input
							id="vsn"
							name="vsn"
							v-model="formData.oceanShipment.vesselName"
							:class="
								twMerge(
									INPUT_CLASS,
									'shadow-[2px_2px_6px_rgba(187,195,206,0.4)] cursor-pointer',
									clsx({ error: false })
								)
							"
						/>
					</div>
					<div class="flex flex-col w-full relative"></div>
					<div class="flex flex-col w-full relative"></div>
				</div>
			</section>
			<div class="flex flex-row w-full justify-end gap-10">
				<button
					@click="goToPreviousStep"
					class="w-[150px] font-monster flex justify-center gap-4 items-center font-semibold bg-secondary hover:bg-secondary/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-white p-3"
				>
					Back
				</button>
				<button
					v-if="isCompleted === false"
					@click="submitForm"
					class="w-[150px] font-monster flex justify-center gap-4 items-center font-semibold bg-primary hover:bg-primary/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-white p-3"
				>
					Submit
				</button>

				<button
					v-if="isCompleted === true"
					@click="goToShipments"
					class="w-[250px] font-monster flex justify-center gap-4 items-center font-semibold bg-primary hover:bg-primary/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-white p-3"
				>
					Go to Shipments
				</button>
			</div>
		</div>

		<!-- Add this to step 1, before the "Next" button -->
		<div
			v-if="
				formData.line_items &&
				formData.line_items.length > 0 &&
				isCompleted
			"
			class="w-full mt-6"
		>
			<div class="flex justify-between items-center mb-4">
				<h6 class="text-secondary font-bold">Shipping Labels</h6>
				<!-- Print All Labels Button -->
				<button
					@click="printAllShippingLabels"
					class="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2 transition-colors"
				>
					<svg width="24" height="24" viewBox="0 -2 32 32">
						<g
							stroke="none"
							stroke-width="1"
							fill="none"
							fill-rule="evenodd"
							sketch:type="MSPage"
						>
							<g
								transform="translate(-100.000000, -205.000000)"
								fill="#ffffff"
							>
								<path
									d="M130,226 C130,227.104 129.104,228 128,228 L125.858,228 C125.413,226.278 123.862,225 122,225 L110,225 C108.138,225 106.587,226.278 106.142,228 L104,228 C102.896,228 102,227.104 102,226 L102,224 C102,222.896 102.896,222 104,222 L128,222 C129.104,222 130,222.896 130,224 L130,226 L130,226 Z M122,231 L110,231 C108.896,231 108,230.104 108,229 C108,227.896 108.896,227 110,227 L122,227 C123.104,227 124,227.896 124,229 C124,230.104 123.104,231 122,231 L122,231 Z M108,209 C108,207.896 108.896,207 110,207 L122,207 C123.104,207 124,207.896 124,209 L124,220 L108,220 L108,209 L108,209 Z M128,220 L126,220 L126,209 C126,206.791 124.209,205 122,205 L110,205 C107.791,205 106,206.791 106,209 L106,220 L104,220 C101.791,220 100,221.791 100,224 L100,226 C100,228.209 101.791,230 104,230 L106.142,230 C106.587,231.723 108.138,233 110,233 L122,233 C123.862,233 125.413,231.723 125.858,230 L128,230 C130.209,230 132,228.209 132,226 L132,224 C132,221.791 130.209,220 128,220 L128,220 Z"
									id="print"
									sketch:type="MSShapeGroup"
								></path>
							</g>
						</g>
					</svg>
					Print All Labels ({{ formData.line_items.length }})
				</button>
			</div>

			<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
				<div
					v-for="(item, index) in formData.line_items"
					:key="item.id"
					@click="toggleModal(index)"
					class="bg-white rounded-lg shadow-md border border-gray-200 p-4 cursor-pointer hover:shadow-lg transition-shadow"
				>
					<div class="flex justify-between items-center mb-2">
						<h6 class="font-semibold text-primary">
							Piece Number: {{ item.itemName }}
						</h6>
						<span
							class="text-xs px-2 py-1 bg-secondary bg-opacity-10 text-secondary rounded-full"
						>
							{{ item.packageType || "Container" }}
						</span>
					</div>

					<div class="text-sm space-y-1">
						<div class="flex justify-between">
							<span class="text-gray-600">Gross Weight:</span>
							<span class="font-medium"
								>{{ item.grossWeight || "0" }}
								{{ item.weightUnit || "lbs" }}</span
							>
						</div>
						<div class="flex justify-between">
							<span class="text-gray-600">Dimensions:</span>
							<span class="font-medium">
								{{ item.length || "0" }} ×
								{{ item.width || "0" }} ×
								{{ item.height || "0" }}
								{{ item.dimensionUnit || "in" }}
							</span>
						</div>
					</div>

					<div
						class="mt-3 pt-2 border-t border-gray-200 flex items-center justify-between"
					>
						<span class="text-xs text-gray-500"
							>PRO Number: {{ proNumber }}</span
						>
						<span class="text-primary flex items-center text-sm">
							View Label
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								class="ml-1"
							>
								<path
									d="M8.5 5L15.5 12L8.5 19"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- Add this modal component to your template -->
	<div
		v-if="showActionModal"
		class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
	>
		<div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6 mx-4">
			<div class="flex justify-between items-center mb-5">
				<h3 class="text-lg font-bold text-secondary">File Actions</h3>
				<button
					@click="showActionModal = false"
					class="text-gray-500 hover:text-gray-700"
				>
					<svg
						class="w-5 h-5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M6 18L18 6M6 6l12 12"
						></path>
					</svg>
				</button>
			</div>

			<div class="space-y-4">
				<p class="text-sm text-gray-600 mb-4">
					Please select an action below:
				</p>

				<!-- Download Template Button -->
				<button
					@click="downloadTemplate"
					class="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-medium py-3 px-4 rounded-lg transition-colors"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						class="h-5 w-5"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
						/>
					</svg>
					Download Template
				</button>

				<!-- Upload File Button -->
				<button
					v-if="!fileSrc.value"
					@click="() => fileInputTag.click()"
					class="w-full flex items-center justify-center gap-2 border border-primary text-primary hover:bg-gray-50 font-medium py-3 px-4 rounded-lg transition-colors"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						class="h-5 w-5"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
						/>
					</svg>
					Upload File
				</button>

				<div
					v-if="fileSrc.value"
					class="w-full flex flex-row items-center justify-center gap-4"
				>
					<a
						:href="fileSrc.value"
						target="_blank"
						class="w-full font-monster flex justify-center gap-4 items-center font-semibold hover:bg-secondary/60 transition-all ease-in-out duration-[750ms] rounded-lg border border-secondary p-3 cursor-pointer"
					>
						<svg
							width="16px"
							height="16px"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g>
								<path
									d="M10.0002 5H8.2002C7.08009 5 6.51962 5 6.0918 5.21799C5.71547 5.40973 5.40973 5.71547 5.21799 6.0918C5 6.51962 5 7.08009 5 8.2002V15.8002C5 16.9203 5 17.4801 5.21799 17.9079C5.40973 18.2842 5.71547 18.5905 6.0918 18.7822C6.5192 19 7.07899 19 8.19691 19H15.8031C16.921 19 17.48 19 17.9074 18.7822C18.2837 18.5905 18.5905 18.2839 18.7822 17.9076C19 17.4802 19 16.921 19 15.8031V14M20 9V4M20 4H15M20 4L13 11"
									stroke="#020754"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</g>
						</svg>
						Preview
					</a>
					<button
						v-on:click="resetFile"
						class="w-full font-monster flex justify-center gap-4 items-center font-semibold hover:bg-primary/60 transition-all ease-in-out duration-[750ms] rounded-lg border border-primary p-3 cursor-pointer text-primary"
					>
						<span role="button" class="text-lg">&times;</span>Reset
					</button>
				</div>
				<button
					v-if="fileSrc.value"
					id="cupload"
					@click="uploadTemplate"
					class="w-full font-monster flex justify-center gap-4 items-center font-semibold bg-secondary hover:bg-secondary/60 transition-all ease-in-out duration-[750ms] rounded-lg text-white p-3"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						class="h-5 w-5"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
						/>
					</svg>
					Continue to validate
				</button>

				<!-- Hidden file input for uplo	ad -->
				<input
					@change="fileChange"
					type="file"
					id="document"
					name="document"
					class="hidden"
					:accept="MIMES.join(', ')"
					ref="fileInputTag"
				/>
			</div>
		</div>
	</div>

	<!-- Registration Complete Modal -->
<div v-if="showRegistrationCompleteModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
  <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6 mx-4">
    <!-- Header -->
    <div class="text-center mb-6">
      <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg class="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
      </div>
      <h3 class="text-xl font-bold text-secondary">Registration Complete</h3>
    </div>

    <!-- PRO Number Section (spans full width) -->
    <div class="mb-6">
      <label class="block text-sm font-medium text-gray-700 mb-2">Your Registration #</label>
      <div class="relative">
        <input 
          v-model="proNumber"
          readonly
          class="w-full rounded-[10px] px-4 py-3 bg-gray-50 border-[1px] border-stroke font-monster border-solid outline-none font-medium text-center text-lg"
          :class="{ 'animate-pulse': isLoadingProNumber }"
        />
        <div v-if="isLoadingProNumber" class="absolute right-3 top-1/2 transform -translate-y-1/2">
          <svg class="animate-spin h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    </div>

    <!-- Action Buttons Grid (2x2) -->
    <div class="grid grid-cols-2 gap-3 mb-4">
      <!-- Print Shipping Labels -->
      <button 
        @click="printAllShippingLabels"
        class="flex flex-col items-center justify-center p-4 border group border-primary text-primary hover:bg-primary hover:text-white rounded-lg transition-colors"
      >
        <svg width="24" height="24" class="mb-2"  viewBox="0 -2 32 32">
			<g
				stroke="none"
				stroke-width="1"
				class="fill-[#a91d38] group-hover:fill-[#fff]"
				fill-rule="evenodd"
				sketch:type="MSPage"
			>
				<g
					transform="translate(-100.000000, -205.000000)"
					
				>
					<path
						d="M130,226 C130,227.104 129.104,228 128,228 L125.858,228 C125.413,226.278 123.862,225 122,225 L110,225 C108.138,225 106.587,226.278 106.142,228 L104,228 C102.896,228 102,227.104 102,226 L102,224 C102,222.896 102.896,222 104,222 L128,222 C129.104,222 130,222.896 130,224 L130,226 L130,226 Z M122,231 L110,231 C108.896,231 108,230.104 108,229 C108,227.896 108.896,227 110,227 L122,227 C123.104,227 124,227.896 124,229 C124,230.104 123.104,231 122,231 L122,231 Z M108,209 C108,207.896 108.896,207 110,207 L122,207 C123.104,207 124,207.896 124,209 L124,220 L108,220 L108,209 L108,209 Z M128,220 L126,220 L126,209 C126,206.791 124.209,205 122,205 L110,205 C107.791,205 106,206.791 106,209 L106,220 L104,220 C101.791,220 100,221.791 100,224 L100,226 C100,228.209 101.791,230 104,230 L106.142,230 C106.587,231.723 108.138,233 110,233 L122,233 C123.862,233 125.413,231.723 125.858,230 L128,230 C130.209,230 132,228.209 132,226 L132,224 C132,221.791 130.209,220 128,220 L128,220 Z"
						id="print"
						sketch:type="MSShapeGroup"
					></path>
				</g>
			</g>
		</svg>
        <span class="text-xs font-medium text-center">Print Shipping Labels</span>
      </button>

      <!-- Print Registration Form -->
      <button 
        
        class="flex flex-col items-center justify-center p-4 border border-secondary text-secondary hover:bg-secondary hover:text-white rounded-lg transition-colors"
      >
        <svg class="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
        <span class="text-xs font-medium text-center">Print Registration Form</span>
      </button>
    </div>

    <!-- Navigation Buttons Grid (2x1) -->
    <div class="grid grid-cols-2 gap-3">
      <!-- Create New Consumer Shipment -->
      <button 
        @click="createNewMilitaryShipment"
        class="flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
      >
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        <span class="text-sm font-medium">Create New</span>
      </button>

      <!-- Exit to Home -->
      <button 
        @click="exitToHome"
        class="flex items-center justify-center px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
        </svg>
        <span class="text-sm font-medium">Exit to Home</span>
      </button>
    </div>
  </div>
</div>

	<!-- Full-Screen Modal -->
	<div
		v-if="isModalOpen"
		class="fixed inset-0 z-50 overflow-y-auto bg-white flex justify-center items-start"
	>
		<div class="bg-white w-full h-full mx-auto p-2 rounded-lg shadow-xl">
			<!-- Modal Header -->
			<div class="flex items-center justify-between border-b px-5 py-2">
				<h3 class="text-xl font-bold text-primary">Preview Label</h3>
				<div class="flex gap-10">
					<button
						@click="printShippingLabel"
						class="focus:outline-none p-1 rounded-full hover:bg-gray-100"
						aria-label="Print"
					>
						<svg width="24" height="24" viewBox="0 -2 32 32">
							<g
								stroke="none"
								stroke-width="1"
								fill="none"
								fill-rule="evenodd"
								sketch:type="MSPage"
							>
								<g
									transform="translate(-100.000000, -205.000000)"
									fill="#020754"
								>
									<path
										d="M130,226 C130,227.104 129.104,228 128,228 L125.858,228 C125.413,226.278 123.862,225 122,225 L110,225 C108.138,225 106.587,226.278 106.142,228 L104,228 C102.896,228 102,227.104 102,226 L102,224 C102,222.896 102.896,222 104,222 L128,222 C129.104,222 130,222.896 130,224 L130,226 L130,226 Z M122,231 L110,231 C108.896,231 108,230.104 108,229 C108,227.896 108.896,227 110,227 L122,227 C123.104,227 124,227.896 124,229 C124,230.104 123.104,231 122,231 L122,231 Z M108,209 C108,207.896 108.896,207 110,207 L122,207 C123.104,207 124,207.896 124,209 L124,220 L108,220 L108,209 L108,209 Z M128,220 L126,220 L126,209 C126,206.791 124.209,205 122,205 L110,205 C107.791,205 106,206.791 106,209 L106,220 L104,220 C101.791,220 100,221.791 100,224 L100,226 C100,228.209 101.791,230 104,230 L106.142,230 C106.587,231.723 108.138,233 110,233 L122,233 C123.862,233 125.413,231.723 125.858,230 L128,230 C130.209,230 132,228.209 132,226 L132,224 C132,221.791 130.209,220 128,220 L128,220 Z"
										id="print"
										sketch:type="MSShapeGroup"
									></path>
								</g>
							</g>
						</svg>
					</button>
					<button
						@click="toggleModal"
						class="focus:outline-none p-1 rounded-full hover:bg-gray-100"
						aria-label="Close"
					>
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M18 6L6 18"
								stroke="#020754"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
							<path
								d="M6 6L18 18"
								stroke="#020754"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
					</button>
				</div>
			</div>

			<!-- Modal Content -->
			<div class="mt-2 bg-white">
				<!-- Shipping Label Preview -->
				<div
					class="shipping-label-container rounded-lg relative overflow-hidden max-h-full h-full"
					style="font-family: Arial, sans-serif !important"
				>
					<!-- Shipping Label Content -->
					<div
						class="relative w-full h-full"
						style="position: relative; width: 100% !important"
					>
						<!-- SCAC Code in top right -->
						<div
							style="
								position: absolute;
								right: 10px;
								display: flex;
								flex-direction: column;
								justify-content: center;
								align-items: center;
							"
							id="svg-node"
						>
							<div style="display: flex; flex-direction: column">
								<span
									style="
										font-family: Arial, sans-serif !important;
										font-size: 21px;
										font-weight: 500;
										text-decoration: underline;
									"
									>SCAC:</span
								>
								<span
									style="
										font-size: 25px;
										font-family: Arial, sans-serif !important;
										font-weight: 700;
									"
									>{{ formData.shipment_type_info.tsp }}</span
								>
							</div>
						</div>

						<!-- Main Content -->
						<div
							style="
								gap: 12px;
								width: 100%;
								display: flex;
								flex-direction: column;
							"
						>
							<!-- Name Header -->
							<div
								class="header"
								style="line-height: 0.5; margin-top: -12px"
							>
								<h1
									style="
										font-size: 60px;
										font-family: Arial, sans-serif !important;
									"
								>
									{{
										formData.shipper.lastName.toUpperCase()
									}},
								</h1>
								<h1
									style="
										font-size: 60px;
										font-family: Arial, sans-serif !important;
									"
								>
									{{
										formData.shipper.firstName.toUpperCase()
									}}
								</h1>
							</div>

							<!-- Registration Numbers -->
							<div
								style="
									display: flex;
									flex-direction: row;
									justify-content: space-between;
								"
							>
								<div
									style="
										display: flex;
										flex-direction: column;
										line-height: 0.5;
									"
								>
									<p
										style="
											margin: 0;
											font-family: Arial, sans-serif !important;
											font-size: 1.2em;
											text-decoration: underline;
											font-weight: bold;
										"
									>
										REG#:
									</p>
									<h1
										style="
											font-size: 60px;
											margin: 25px 0px;
											font-family: Arial, sans-serif !important;
										"
									>
										{{ formData.shipment_type_info
												.registrationNumber || "" }}
									</h1>
								</div>
								<div></div>
								<div></div>
							</div>

							<!-- Dates -->
							<div
								style="
									display: flex;
									flex-direction: row;
									gap: 20px;
								"
							>
								<p
									style="
										margin: 0;
										font-weight: bold;
										font-family: Arial, sans-serif !important;
									"
								>
									PICK UP DATE:
									{{
										formatDate(formData.shipper.pickupDate)
									}}
								</p>
								<p
									style="
										margin: 0;
										font-family: Arial, sans-serif !important;
										font-weight: bold;
									"
								>
									RDD: {{ formatDate(formData.shipper.rdd) }}
								</p>
							</div>

							<!-- Origin/Destination -->
							<div
								style="
									display: flex;
									flex-direction: column;
									gap: 20px;
									margin-top: 20px;
								"
							>
								<div
									style="
										display: flex;
										flex-direction: column;
									"
								>
									<div
										style="
											display: flex;
											flex-direction: row;
										"
									></div>
									<p
										style="
											margin: 0;
											display: flex;
											gap: 10px;
											align-items: center;
											font-family: Arial, sans-serif !important;
										"
									>
										<u
											style="
												font-family: Arial, sans-serif !important;
											"
											>ORIGIN:</u
										><span
											style="
												font-size: 25px;
												font-family: Arial, sans-serif !important;
												font-weight: bold;
											"
										>
											{{
												formatLocation(formData.origin)
											}}
										</span>
									</p>
								</div>
								<div
									style="
										display: flex;
										flex-direction: column;
									"
								>
									<p
										style="
											margin: 0;
											display: flex;
											gap: 10px;
											align-items: center;
											font-family: Arial, sans-serif !important;
										"
									>
										<u
											style="
												font-family: Arial, sans-serif !important;
											"
											>DEST:</u
										><span
											style="
												font-size: 25px;
												font-weight: bold;
												font-family: Arial, sans-serif !important;
											"
										>
											{{
												formatLocation(
													formData.destination
												)
											}}
										</span>
									</p>
									
								</div>
							</div>

							<!-- Weight Information -->
							<div
								style="
									display: flex;
									flex-direction: row;
									justify-content: space-between;
									margin-top: 30px;
									max-width: 100%;
								"
							>
								<!-- Calculate totals from line items -->
								<div
									style="
										display: flex;
										flex-direction: column;
										gap: 5px;
										align-items: center;
									"
								>
									<span
										style="
											font-size: 25px;
											font-weight: 700;
											text-decoration: underline;
										"
										>GROSS (Lbs):</span
									>
									<span
										style="
											font-size: 23px;
											font-weight: bold;
										"
										>{{
											calculateTotalWeight("gross")
										}}</span
									>
								</div>
								<div
									style="
										display: flex;
										flex-direction: column;
										gap: 5px;
										align-items: center;
									"
								>
									<span
										style="
											font-size: 25px;
											font-weight: 700;
											text-decoration: underline;
										"
										>TARE (Lbs):</span
									>
									<span
										style="
											font-size: 23px;
											font-weight: bold;
										"
										>{{
											calculateTotalWeight("tare")
										}}</span
									>
								</div>
								<div
									style="
										display: flex;
										flex-direction: column;
										gap: 5px;
										align-items: center;
									"
								>
									<span
										style="
											font-size: 25px;
											font-weight: 700;
											text-decoration: underline;
										"
										>NET (Lbs):</span
									>
									<span
										style="
											font-size: 23px;
											font-weight: bold;
										"
										>{{ calculateTotalWeight("net") }}</span
									>
								</div>
								<div
									style="
										display: flex;
										flex-direction: column;
										gap: 5px;
										align-items: center;
									"
								>
									<span
										style="
											font-size: 25px;
											font-weight: 700;
											text-decoration: underline;
										"
										>CUBE (Ft):</span
									>
									<span
										style="
											font-size: 23px;
											font-weight: bold;
										"
										>{{ calculateTotalCube() }}</span
									>
								</div>
								<div
									style="
										display: flex;
										flex-direction: column;
										gap: 5px;
										align-items: center;
									"
								>
									<span
										style="
											font-size: 25px;
											font-weight: 700;
											text-decoration: underline;
										"
										>TYPE:</span
									>
									<span
										style="
											font-size: 23px;
											font-weight: bold;
										"
										>{{
											formData.shipment_type_info.shipmentType.startsWith(
												"CODE_"
											)
												? formData.shipment_type_info.shipmentType.substring(
														formData.shipment_type_info.shipmentType.indexOf(
															"CODE_"
														) + "CODE_".length
												  )
												: formData.shipment_type_info
														.shipmentType
										}}</span
									>
								</div>
							</div>

							<!-- Barcode -->
							<div
								style="
									display: flex;
									flex-direction: row;
									justify-content: space-around;
									align-items: center;
									padding: 0 10px 0 10px;
								"
							>
								<div
									style="
										display: flex;
										justify-content: center;
										align-items: center;
									"
								>
									<svg id="barcode" style="width: 100%"></svg>
								</div>
								<div
									style="
										display: flex;
										justify-content: center;
										align-items: center;
									"
								>
									<span
										style="
											font-size: 75px;
											font-weight: 700;
											text-decoration: underline;
										"
										>{{ calculateLineItem() }}</span
									>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
